import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Monitor, ShoppingCart, Printer, Plus, Minus, X, Search, LogOut, Copy, Phone, MapPin, User, Package, CheckCircle, Truck, Power, Store, FileText, AlertTriangle, RefreshCw, Calendar, Trash2, Camera, Wifi, WifiOff, CreditCard, Banknote, Smartphone, Bell, BellOff, Volume2, VolumeX, BarChart3, ClipboardCheck, UtensilsCrossed, Gift } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api, { getImageUrl } from '@/lib/api';
import { formatCurrency, getStatusColor, getStatusLabel } from '@/lib/utils';
import StaffSwitcher from '@/components/StaffSwitcher';

const POSSystem = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [orderType, setOrderType] = useState('dine_in');
  const [showOrders, setShowOrders] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [lastOrderCheck, setLastOrderCheck] = useState(Date.now());
  
  // Outlet selection state
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [isAcceptingOrders, setIsAcceptingOrders] = useState(true);
  const [isAcceptingDineIn, setIsAcceptingDineIn] = useState(true);
  const [isAcceptingDelivery, setIsAcceptingDelivery] = useState(true);
  
  // Dynamic categories from API
  const [categories, setCategories] = useState(['All']);
  
  // Customer name (optional)
  const [customerName, setCustomerName] = useState('');
  
  // Product management mode
  const [showManageProducts, setShowManageProducts] = useState(false);
  
  // Stock alerts state
  const [criticalStockAlerts, setCriticalStockAlerts] = useState([]);
  const [showStockAlerts, setShowStockAlerts] = useState(false);
  const wsRef = useRef(null);
  
  // Refund state
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundOrder, setRefundOrder] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  
  // Partial rejection state
  const [showPartialRejectDialog, setShowPartialRejectDialog] = useState(false);
  const [partialRejectOrder, setPartialRejectOrder] = useState(null);
  const [partialRejectItems, setPartialRejectItems] = useState([]);
  const [partialRejectReason, setPartialRejectReason] = useState('');
  
  // Mobile cart visibility
  const [showMobileCart, setShowMobileCart] = useState(false);
  
  // Print dialog
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [orderToPrint, setOrderToPrint] = useState(null);
  const printRef = useRef(null);

  // Order search state
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [orderSearchDate, setOrderSearchDate] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [searchingOrders, setSearchingOrders] = useState(false);

  // Offers state
  const [activeOffers, setActiveOffers] = useState([]);
  const [eligibleOffers, setEligibleOffers] = useState([]);
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  const [appliedOffers, setAppliedOffers] = useState([]);
  const [showOffersPanel, setShowOffersPanel] = useState(false);
  
  // Wastage tracking state
  const [showWastageDialog, setShowWastageDialog] = useState(false);
  const [wastageProduct, setWastageProduct] = useState(null);
  const [wastageQuantity, setWastageQuantity] = useState(1);
  const [wastageReason, setWastageReason] = useState('');
  const [wastageNotes, setWastageNotes] = useState('');
  const [wastageLoading, setWastageLoading] = useState(false);
  const [showWastageHistory, setShowWastageHistory] = useState(false);
  const [wastageHistory, setWastageHistory] = useState([]);

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('cash');
  
  // Offline mode state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineOrders, setOfflineOrders] = useState([]);
  const [syncingOrders, setSyncingOrders] = useState(false);

  // Push notification state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [lastNewOrderCount, setLastNewOrderCount] = useState(0);
  const audioRef = useRef(null);

  // Edit order state
  const [showEditOrderDialog, setShowEditOrderDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderItems, setEditOrderItems] = useState([]);
  const [editCustomerName, setEditCustomerName] = useState('');
  const [editOrderType, setEditOrderType] = useState('dine_in');
  const [editPaymentMethod, setEditPaymentMethod] = useState('cash');
  const [editingLoading, setEditingLoading] = useState(false);

  // Delete order state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [deletingLoading, setDeletingLoading] = useState(false);

  // Reconciliation state
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [reconciliationData, setReconciliationData] = useState([]);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [savingReconciliation, setSavingReconciliation] = useState(false);

  // Today's Summary state
  const [showTodaySummary, setShowTodaySummary] = useState(false);
  const [todaySummary, setTodaySummary] = useState(null);
  const [loadingTodaySummary, setLoadingTodaySummary] = useState(false);

  // Quick Wastage Entry state (for damaged/dump)
  const [showQuickWastage, setShowQuickWastage] = useState(false);

  // POS Product Customization state
  const [showPosCustomization, setShowPosCustomization] = useState(false);
  const [customizingProduct, setCustomizingProduct] = useState(null);
  const [posSelectedAddons, setPosSelectedAddons] = useState([]);
  const [posSelectedCustomizations, setPosSelectedCustomizations] = useState({});
  const [posItemNotes, setPosItemNotes] = useState('');

  // Play notification sound for new orders
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body) => {
    if (notificationsEnabled && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/icon-192x192.png',
        tag: 'pos-new-order',
        renotify: true,
        requireInteraction: true
      });
    }
  }, [notificationsEnabled]);

  // Request notification permission
  const enablePushNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Push notifications enabled!');
      } else {
        toast.error('Notification permission denied');
      }
    }
  };

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted') {
      setNotificationsEnabled(true);
    }
  }, []);

  // Load offline orders from localStorage on mount
  useEffect(() => {
    const savedOfflineOrders = localStorage.getItem('pos_offline_orders');
    if (savedOfflineOrders) {
      try {
        setOfflineOrders(JSON.parse(savedOfflineOrders));
      } catch (e) {
        console.error('Error loading offline orders:', e);
      }
    }
    
    // Cache products for offline use
    const cachedProducts = localStorage.getItem('pos_cached_products');
    if (cachedProducts && !navigator.onLine) {
      try {
        setProducts(JSON.parse(cachedProducts));
      } catch (e) {
        console.error('Error loading cached products:', e);
      }
    }
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      toast.success('Back online! Syncing orders...', { duration: 3000 });
      syncOfflineOrders();
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      toast.warning('You are offline. Orders will be saved locally.', { duration: 5000 });
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [offlineOrders]);

  // Cache products whenever they're fetched
  useEffect(() => {
    if (products.length > 0) {
      localStorage.setItem('pos_cached_products', JSON.stringify(products));
    }
  }, [products]);

  // Save offline orders to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('pos_offline_orders', JSON.stringify(offlineOrders));
  }, [offlineOrders]);

  // Sync offline orders when back online
  const syncOfflineOrders = async () => {
    if (offlineOrders.length === 0 || syncingOrders) return;
    
    setSyncingOrders(true);
    
    // Get orders that haven't been synced yet
    const ordersToSync = offlineOrders.filter(o => !o.synced);
    
    if (ordersToSync.length === 0) {
      setSyncingOrders(false);
      toast.info('No orders to sync');
      return;
    }
    
    const successfullysynced = [];
    const failedOrders = [];
    
    for (const order of ordersToSync) {
      try {
        // Clean the order data to match backend schema
        const cleanOrderData = {
          items: order.items,
          subtotal: order.subtotal,
          packaging_fee: order.packaging_fee || 0,
          gst_amount: order.gst_amount || 0,
          total_amount: order.total_amount,
          order_type: order.order_type,
          payment_method: order.payment_method || 'cash',
          customer_name: order.customer_name || 'Walk-in Customer',
          customer_phone: order.customer_phone || '0000000000',
          outlet_id: order.outlet_id,
          source: 'pos_offline_sync',
          offline_order_number: order.order_number // Keep reference to offline order number
        };
        
        const response = await api.post('/orders', cleanOrderData);
        
        // Mark as confirmed (for POS orders)
        await api.post('/payment/verify', {
          razorpay_order_id: 'pos_offline_sync',
          razorpay_payment_id: `pos_${order.payment_method}_${Date.now()}`,
          razorpay_signature: 'pos_signature',
          order_id: response.data.id
        });
        
        // Mark this order as successfully synced
        successfullysynced.push(order.id);
        toast.success(`Synced order: ${order.order_number}`, { duration: 2000 });
      } catch (error) {
        console.error('Failed to sync order:', error);
        failedOrders.push({ ...order, syncError: error.message });
      }
    }
    
    // Remove successfully synced orders from the list
    setOfflineOrders(prev => prev.filter(o => !successfullysynced.includes(o.id)));
    setSyncingOrders(false);
    
    if (failedOrders.length === 0 && successfullysynced.length > 0) {
      toast.success(`All ${successfullysynced.length} offline orders synced!`);
    } else if (failedOrders.length > 0) {
      toast.error(`${failedOrders.length} order(s) failed to sync. Will retry later.`);
    }
    
    // Refresh orders list
    fetchOrders();
  };

  // Copy to clipboard helper with multiple fallbacks
  const copyToClipboard = (text, label) => {
    // Method 1: Try navigator.clipboard (modern browsers, requires HTTPS)
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text)
        .then(() => {
          toast.success(`${label} copied!`);
        })
        .catch((err) => {
          console.log('Clipboard API failed, trying fallback:', err);
          fallbackCopy(text, label);
        });
    } else {
      fallbackCopy(text, label);
    }
  };

  // Fallback copy method
  const fallbackCopy = (text, label) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // Make it invisible but part of the document
      textArea.style.cssText = 'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;boxShadow:none;background:transparent;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          toast.success(`${label} copied!`);
        } else {
          // Show the text in an alert so user can manually copy
          toast.info(`Please copy manually: ${text.substring(0, 50)}...`);
        }
      } catch (execErr) {
        toast.info(`Please copy manually: ${text.substring(0, 50)}...`);
      }
      
      document.body.removeChild(textArea);
    } catch (err) {
      console.error('All copy methods failed:', err);
      toast.error('Copy failed - please copy manually');
    }
  };

  // Copy all delivery details at once
  const copyAllDeliveryDetails = (order) => {
    const address = order.customer_address || order.delivery_address || 'N/A';
    let details = `Order: ${order.order_number}
Customer: ${order.customer_name}
Phone: ${order.customer_phone}
Address: ${address}
Amount: ${formatCurrency(order.total_amount)}
Items: ${order.items.map(i => `${i.quantity}x ${i.product_name}`).join(', ')}`;
    
    // Add GPS coordinates if available
    const lat = order.delivery_latitude;
    const lng = order.delivery_longitude;
    if (lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng))) {
      details += `\nGPS: ${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
      details += `\nMap: https://www.google.com/maps?q=${lat},${lng}`;
    } else if (address && address !== 'N/A') {
      details += `\nMap: https://www.google.com/maps/search/${encodeURIComponent(address)}`;
    }
    
    copyToClipboard(details, 'All delivery details');
  };

  useEffect(() => {
    fetchOutlets();
    fetchCategories();
  }, []);

  // Load products and orders when outlet is selected
  useEffect(() => {
    if (selectedOutlet) {
      fetchProducts();
      fetchOrders();
      fetchOutletStatus();
      fetchCriticalStockAlerts();
      fetchActiveOffers();
      
      // Poll for new orders every 10 seconds
      const pollInterval = setInterval(() => {
        checkForNewOrders();
      }, 10000);
      
      // Poll for stock alerts every 30 seconds
      const stockAlertInterval = setInterval(() => {
        fetchCriticalStockAlerts();
      }, 30000);
      
      // Connect to WebSocket for real-time stock updates
      connectWebSocket();
      
      return () => {
        clearInterval(pollInterval);
        clearInterval(stockAlertInterval);
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [selectedOutlet]);

  // Check offers eligibility when cart changes
  useEffect(() => {
    if (cart.length > 0 && activeOffers.length > 0) {
      checkEligibleOffers();
    } else {
      setEligibleOffers([]);
    }
  }, [cart, activeOffers]);

  // WebSocket connection for real-time stock updates
  const connectWebSocket = useCallback(() => {
    if (!selectedOutlet) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Construct WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/stock/${selectedOutlet.id}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected for stock updates');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'stock_update') {
            // Update product stock in state
            setProducts(prev => prev.map(p => ({
              ...p,
              stock: data.stock_data[p.id] !== undefined ? data.stock_data[p.id] : p.stock
            })));
            
            // Update critical alerts
            if (data.critical_alerts && data.critical_alerts.length > 0) {
              setCriticalStockAlerts(data.critical_alerts);
              // Show toast for new critical alerts
              data.critical_alerts.forEach(alert => {
                if (alert.stock === 0) {
                  toast.error(`${alert.product_name} is now OUT OF STOCK!`);
                } else if (alert.stock <= 5) {
                  toast.warning(`Low stock: ${alert.product_name} - Only ${alert.stock} left`);
                }
              });
            }
          } else if (data.type === 'new_ticket') {
            // New support ticket notification
            if (soundEnabled) {
              playNotificationSound();
            }
            toast.info(`🎫 New Ticket: ${data.subject}`, {
              description: `From ${data.customer_name} - Order #${data.order_id}`,
              duration: 8000,
              action: {
                label: 'View',
                onClick: () => window.open('/admin?tab=tickets', '_blank')
              }
            });
            showBrowserNotification(
              `New Support Ticket: ${data.subject}`,
              `${data.customer_name} needs help with Order #${data.order_id}`
            );
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (selectedOutlet) {
            connectWebSocket();
          }
        }, 5000);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [selectedOutlet]);

  // Fetch critical stock alerts
  const fetchCriticalStockAlerts = async () => {
    if (!selectedOutlet) return;
    try {
      const response = await api.get(`/analytics/critical-stock?outlet_id=${selectedOutlet.id}`);
      setCriticalStockAlerts(response.data.critical_items || []);
    } catch (error) {
      console.error('Error fetching stock alerts:', error);
    }
  };

  // Process refund (full or partial)
  const processRefund = async (order, amount = null, reason = 'Order rejected/cancelled by POS') => {
    if (!order.razorpay_payment_id) {
      toast.error('Cannot refund COD orders through Razorpay');
      return;
    }
    
    setRefundLoading(true);
    try {
      const response = await api.post('/payment/refund', {
        order_id: order.id,
        amount: amount, // null = full refund, else partial
        reason: reason
      });
      
      toast.success(response.data.message || 'Refund processed successfully');
      setShowRefundDialog(false);
      setRefundOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Refund error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  };

  // Initialize partial rejection
  const initPartialReject = (order) => {
    setPartialRejectOrder(order);
    // Initialize with all items set to keep full quantity
    setPartialRejectItems(order.items.map(item => ({
      ...item,
      reject_quantity: 0,
      keep_quantity: item.quantity
    })));
    setPartialRejectReason('');
    setShowPartialRejectDialog(true);
  };

  // Update partial reject item quantity
  const updatePartialRejectQuantity = (productId, rejectQty) => {
    setPartialRejectItems(prev => prev.map(item => {
      if (item.product_id === productId) {
        const maxQty = item.quantity;
        const validRejectQty = Math.min(Math.max(0, rejectQty), maxQty);
        return {
          ...item,
          reject_quantity: validRejectQty,
          keep_quantity: maxQty - validRejectQty
        };
      }
      return item;
    }));
  };

  // Process partial rejection
  const processPartialRejection = async () => {
    if (!partialRejectOrder) return;
    
    const itemsToReject = partialRejectItems.filter(item => item.reject_quantity > 0);
    if (itemsToReject.length === 0) {
      toast.error('Please select at least one item to reject');
      return;
    }
    
    // Check if rejecting all items
    const totalKept = partialRejectItems.reduce((sum, item) => sum + item.keep_quantity, 0);
    
    setRefundLoading(true);
    try {
      const response = await api.post('/orders/partial-reject', {
        order_id: partialRejectOrder.id,
        rejected_items: itemsToReject.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.reject_quantity,
          price: item.price
        })),
        reason: partialRejectReason || 'Items unavailable'
      });
      
      toast.success(response.data.message || 'Partial rejection processed');
      setShowPartialRejectDialog(false);
      setPartialRejectOrder(null);
      setPartialRejectItems([]);
      fetchOrders();
      
      // If there's a refund amount, show it
      if (response.data.refund_amount > 0) {
        toast.info(`Refund of ₹${response.data.refund_amount.toFixed(2)} will be processed`, { duration: 5000 });
      }
    } catch (error) {
      console.error('Partial rejection error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process partial rejection');
    } finally {
      setRefundLoading(false);
    }
  };

  // Calculate refund amount for partial rejection
  const calculatePartialRefundAmount = () => {
    return partialRejectItems.reduce((sum, item) => {
      return sum + (item.reject_quantity * item.price);
    }, 0);
  };

  // ============ ORDER SEARCH FUNCTIONS ============
  
  const searchOrders = async () => {
    if (!selectedOutlet) return;
    setSearchingOrders(true);
    try {
      const params = new URLSearchParams();
      params.append('outlet_id', selectedOutlet.id);
      if (orderSearchQuery) params.append('q', orderSearchQuery);
      if (orderSearchDate) params.append('date_from', orderSearchDate);
      if (orderSearchDate) params.append('date_to', orderSearchDate);
      if (orderStatusFilter !== 'all') params.append('status', orderStatusFilter);
      params.append('limit', '100');
      
      const response = await api.get(`/orders/search?${params.toString()}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error searching orders:', error);
      toast.error('Failed to search orders');
    } finally {
      setSearchingOrders(false);
    }
  };

  const clearOrderSearch = () => {
    setOrderSearchQuery('');
    setOrderSearchDate('');
    setOrderStatusFilter('all');
    fetchOrders();
  };

  // ============ WASTAGE TRACKING FUNCTIONS ============
  
  const openWastageDialog = (product) => {
    setWastageProduct(product);
    setWastageQuantity(1);
    setWastageReason('');
    setWastageNotes('');
    setShowWastageDialog(true);
  };

  const submitWastage = async () => {
    if (!wastageProduct || !selectedOutlet || !wastageReason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setWastageLoading(true);
    try {
      await api.post('/dump', {
        outlet_id: selectedOutlet.id,
        product_id: wastageProduct.id,
        quantity: wastageQuantity,
        reason: wastageReason,
        photos: [], // Photos can be added later with file upload
        reported_by: 'POS Staff'
      });
      
      toast.success(`${wastageQuantity} x ${wastageProduct.name} logged as wastage`);
      setShowWastageDialog(false);
      setWastageProduct(null);
      fetchProducts(); // Refresh stock
    } catch (error) {
      console.error('Error logging wastage:', error);
      toast.error(error.response?.data?.detail || 'Failed to log wastage');
    } finally {
      setWastageLoading(false);
    }
  };

  const fetchWastageHistory = async () => {
    if (!selectedOutlet) return;
    try {
      const response = await api.get(`/dump?outlet_id=${selectedOutlet.id}&limit=50`);
      setWastageHistory(response.data);
    } catch (error) {
      console.error('Error fetching wastage history:', error);
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      const activeCategories = response.data
        .filter(c => c.active !== false)
        .map(c => c.name);
      setCategories(['All', ...activeCategories]);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to default categories if API fails
      setCategories(['All', 'Burgers', 'Prawns', 'Fish Fry', 'Combos', 'Beverages']);
    }
  };

  // Fetch outlets
  const fetchOutlets = async () => {
    try {
      const response = await api.get('/outlets');
      setOutlets(response.data);
      
      // Auto-select first outlet if only one exists
      if (response.data.length === 1) {
        setSelectedOutlet(response.data[0]);
        setIsAcceptingOrders(response.data[0].is_accepting_orders !== false);
        setIsAcceptingDineIn(response.data[0].is_accepting_dine_in !== false);
        setIsAcceptingDelivery(response.data[0].is_accepting_delivery !== false);
      }
    } catch (error) {
      console.error('Error fetching outlets:', error);
    }
  };

  // Fetch outlet status
  const fetchOutletStatus = async () => {
    if (!selectedOutlet) return;
    try {
      const response = await api.get(`/outlets/${selectedOutlet.id}/status`);
      setIsAcceptingOrders(response.data.is_accepting_orders !== false);
      setIsAcceptingDineIn(response.data.is_accepting_dine_in !== false);
      setIsAcceptingDelivery(response.data.is_accepting_delivery !== false);
    } catch (error) {
      console.error('Error fetching outlet status:', error);
    }
  };

  // Toggle order acceptance
  const toggleOrderAcceptance = async () => {
    if (!selectedOutlet) return;
    try {
      const response = await api.post(`/outlets/${selectedOutlet.id}/toggle-orders`);
      setIsAcceptingOrders(response.data.is_accepting_orders);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to toggle order acceptance');
    }
  };

  // Toggle dine-in order acceptance
  const toggleDineInAcceptance = async () => {
    if (!selectedOutlet) return;
    try {
      const newValue = !isAcceptingDineIn;
      await api.put(`/outlets/${selectedOutlet.id}`, { is_accepting_dine_in: newValue });
      setIsAcceptingDineIn(newValue);
      toast.success(newValue ? 'Dine-in orders enabled' : 'Dine-in orders disabled');
    } catch (error) {
      toast.error('Failed to toggle dine-in orders');
    }
  };

  // Toggle delivery order acceptance
  const toggleDeliveryAcceptance = async () => {
    if (!selectedOutlet) return;
    try {
      const newValue = !isAcceptingDelivery;
      await api.put(`/outlets/${selectedOutlet.id}`, { is_accepting_delivery: newValue });
      setIsAcceptingDelivery(newValue);
      toast.success(newValue ? 'Delivery orders enabled' : 'Delivery orders disabled');
    } catch (error) {
      toast.error('Failed to toggle delivery orders');
    }
  };

  // Check for new orders and show notification
  const checkForNewOrders = async () => {
    if (!selectedOutlet) return;
    try {
      const response = await api.get('/orders');
      // Filter orders by selected outlet
      const outletOrders = response.data.filter(o => o.outlet_id === selectedOutlet.id);
      
      // Count pending orders (online orders waiting for acceptance - POS orders skip this)
      const pendingOrders = outletOrders.filter(o => o.status === 'placed' && o.source !== 'pos');
      
      if (pendingOrders.length > 0 && pendingOrders.length > newOrderCount) {
        // Play notification sound for new online orders only
        playNotificationSound();
        
        toast.info(`🔔 ${pendingOrders.length} new online order(s) waiting for acceptance!`, {
          duration: 5000
        });
      }
      
      setNewOrderCount(pendingOrders.length);
      setOrders(outletOrders);
    } catch (error) {
      console.error('Error checking for new orders:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      // Fetch products with outlet_id to get outlet-specific stock
      // Include addon-only products for POS (staff can add them manually)
      const outletId = selectedOutlet?.id || 'main';
      const response = await api.get(`/products?outlet_id=${outletId}&include_addon_only=true`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch active offers for POS
  const fetchActiveOffers = async () => {
    try {
      const outletId = selectedOutlet?.id;
      const response = await api.get(`/offers/active${outletId ? `?outlet_id=${outletId}` : ''}`);
      setActiveOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  // Check which offers apply to current cart
  const checkEligibleOffers = async () => {
    if (cart.length === 0 || activeOffers.length === 0) {
      setEligibleOffers([]);
      return;
    }

    try {
      const cartItems = cart.map(item => ({
        product_id: item.id,
        quantity: item.quantity,
        price: item.price
      }));
      const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      const response = await api.post('/offers/check-eligibility', {
        cart_items: cartItems,
        cart_total: cartTotal,
        outlet_id: selectedOutlet?.id || null
      });
      
      // Filter out already applied offers
      const newEligible = response.data.filter(
        offer => !appliedOffers.some(applied => applied.id === offer.id)
      );
      
      setEligibleOffers(newEligible);
      
      // Auto-show popup if there are new eligible combo deals
      const combos = newEligible.filter(o => o.offer_type === 'combo_deal' || o.offer_type === 'bogo');
      if (combos.length > 0 && cart.length > 0) {
        setShowOfferPopup(true);
      }
    } catch (error) {
      console.error('Error checking offer eligibility:', error);
    }
  };

  // Apply a combo deal offer (add product at special price)
  const applyComboOffer = (offer) => {
    const reward = offer.reward;
    if (!reward?.product_id) return;
    
    // Find the product
    const product = products.find(p => p.id === reward.product_id);
    if (!product) {
      toast.error('Offer product not found');
      return;
    }
    
    // Add to cart at special price
    const offerItem = {
      ...product,
      price: reward.special_price || 0,
      originalPrice: product.price,
      isOfferItem: true,
      offerId: offer.id,
      offerName: offer.name
    };
    
    setCart(prev => [...prev, { ...offerItem, quantity: 1 }]);
    setAppliedOffers(prev => [...prev, offer]);
    setShowOfferPopup(false);
    toast.success(`Added ${product.name} at ₹${reward.special_price}!`);
    
    // Track offer usage
    api.post(`/offers/${offer.id}/apply`).catch(console.error);
  };

  // Remove applied offer
  const removeAppliedOffer = (offerId) => {
    setCart(prev => prev.filter(item => item.offerId !== offerId));
    setAppliedOffers(prev => prev.filter(o => o.id !== offerId));
  };

  const fetchOrders = async () => {
    if (!selectedOutlet) return;
    try {
      const response = await api.get('/orders');
      // Filter orders by selected outlet
      const outletOrders = response.data.filter(o => o.outlet_id === selectedOutlet.id);
      // Only count online orders as "pending" - POS orders go directly to accepted
      const pendingOrders = outletOrders.filter(o => o.status === 'placed' && o.source !== 'pos');
      
      // Check for new online orders and trigger notification
      if (pendingOrders.length > lastNewOrderCount && lastNewOrderCount >= 0 && orders.length > 0) {
        playNotificationSound();
        showBrowserNotification(
          '🔔 New Online Order!',
          `${pendingOrders.length - lastNewOrderCount} new order(s) waiting`
        );
      }
      setLastNewOrderCount(pendingOrders.length);
      
      setNewOrderCount(pendingOrders.length);
      setOrders(outletOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Accept order
  const acceptOrder = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: 'accepted' });
      toast.success('Order accepted!');
      fetchOrders();
      setShowOrderDetails(false);
    } catch (error) {
      console.error('Error accepting order:', error);
      toast.error('Failed to accept order');
    }
  };

  // Reject order
  const rejectOrder = async (orderId, reason = '') => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: 'rejected' });
      toast.success('Order rejected');
      fetchOrders();
      setShowOrderDetails(false);
    } catch (error) {
      console.error('Error rejecting order:', error);
      toast.error('Failed to reject order');
    }
  };

  // Open customization dialog for product with add-ons
  const openCustomizationDialog = (product) => {
    // Use effective_addons and effective_customization_groups from global system if available
    const hasAddons = (product.effective_addons && product.effective_addons.length > 0) ||
                     (product.addons && product.addons.length > 0);
    const hasCustomizations = (product.effective_customization_groups && product.effective_customization_groups.length > 0) ||
                             (product.customization_groups && product.customization_groups.length > 0);
    
    if (hasAddons || hasCustomizations) {
      setCustomizingProduct(product);
      setPosSelectedAddons([]);
      setPosSelectedCustomizations({});
      setPosItemNotes('');
      setShowPosCustomization(true);
    } else {
      // No customizations, add directly
      addToCart(product, [], {}, '');
    }
  };

  const addToCart = (product, addons = [], customizations = {}, notes = '') => {
    const availableStock = product.stock || 0;
    
    // Calculate addon total
    const addonTotal = addons.reduce((sum, addon) => sum + (addon.price || 0), 0);
    
    // Calculate customization total
    const customizationTotal = Object.values(customizations).reduce((sum, opt) => sum + (opt.price || 0), 0);
    
    // Total price including base + addons + customizations
    const totalItemPrice = product.price + addonTotal + customizationTotal;
    
    // Create unique key for cart item (product + addons + customizations)
    const addonKey = addons.map(a => a.name).sort().join(',');
    const customKey = Object.entries(customizations).map(([g, o]) => `${g}:${o.name}`).sort().join(',');
    const cartItemKey = `${product.id}-${addonKey}-${customKey}-${notes}`;
    
    const existingItem = cart.find(item => item.cart_key === cartItemKey);
    const currentQtyInCart = existingItem ? existingItem.quantity : 0;
    
    // Check stock limit
    if (currentQtyInCart >= availableStock) {
      toast.error(`Only ${availableStock} available in stock`);
      return;
    }
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.cart_key === cartItemKey
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        cart_key: cartItemKey,
        product_id: product.id,
        product_name: product.name,
        price: totalItemPrice,
        base_price: product.price,
        quantity: 1,
        max_stock: availableStock,
        selected_addons: addons,
        selected_customizations: Object.entries(customizations).map(([group, opt]) => ({
          group,
          option: opt.name,
          price: opt.price || 0
        })),
        item_notes: notes || null
      }]);
    }
    
    // Show success with addon info
    if (addons.length > 0 || Object.keys(customizations).length > 0) {
      const extras = [...addons.map(a => a.name), ...Object.values(customizations).map(c => c.name)].join(', ');
      toast.success(`${product.name} (${extras}) added`);
    } else {
      toast.success(`${product.name} added`);
    }
  };

  const updateQuantity = (cartKeyOrProductId, delta) => {
    setCart(cart.map(item => {
      if (item.cart_key === cartKeyOrProductId || item.product_id === cartKeyOrProductId) {
        const newQuantity = item.quantity + delta;
        
        // If increasing, check stock limit
        if (delta > 0) {
          const product = products.find(p => p.id === item.product_id);
          const availableStock = product?.stock || item.max_stock || 999;
          if (newQuantity > availableStock) {
            toast.error(`Only ${availableStock} available in stock`);
            return item;
          }
        }
        
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (cartKeyOrProductId) => {
    setCart(cart.filter(item => item.cart_key !== cartKeyOrProductId && item.product_id !== cartKeyOrProductId));
  };

  // Calculate subtotal (items only)
  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Calculate packaging charges (only for takeaway)
  const calculatePackaging = () => {
    if (orderType !== 'takeaway') return 0;
    return cart.reduce((sum, item) => {
      const product = products.find(p => p.id === item.product_id);
      const packagingFee = product?.packaging_fee || 0;
      return sum + (packagingFee * item.quantity);
    }, 0);
  };

  // Calculate GST/Tax (always applied)
  const calculateTax = () => {
    return cart.reduce((sum, item) => {
      const product = products.find(p => p.id === item.product_id);
      if (product?.tax_enabled && product?.tax_percentage > 0) {
        const itemTotal = item.price * item.quantity;
        return sum + (itemTotal * product.tax_percentage / 100);
      }
      return sum;
    }, 0);
  };

  // Calculate grand total
  const calculateTotal = () => {
    return calculateSubtotal() + calculatePackaging() + calculateTax();
  };

  // Get cart totals for display
  const cartSubtotal = calculateSubtotal();
  const cartPackaging = calculatePackaging();
  const cartTax = calculateTax();
  const cartTotal = calculateTotal();
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    // Check if UPI/Card payment is selected but we're offline
    if (isOffline && paymentMethod !== 'cash') {
      toast.error('Only cash payments available in offline mode');
      return;
    }

    setLoading(true);
    
    const orderNumber = `ORD-${Date.now().toString(36).toUpperCase()}`;
    const orderData = {
      items: cart,
      subtotal: cartSubtotal,
      packaging_fee: cartPackaging,
      gst_amount: cartTax,
      total_amount: cartTotal,
      order_type: orderType,
      payment_method: paymentMethod,
      payment_status: paymentMethod === 'cash' ? 'cod' : 'completed',
      customer_name: customerName.trim() || 'Walk-in Customer',
      customer_phone: '0000000000',
      outlet_id: selectedOutlet?.id || 'main',
      order_number: orderNumber,
      created_at: new Date().toISOString(),
      source: 'pos'
    };

    // If offline, save order locally
    if (isOffline) {
      const offlineOrder = {
        ...orderData,
        id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        status: 'pending_sync',
        offline: true,
        synced: false // Track sync status
      };
      
      setOfflineOrders(prev => [...prev, offlineOrder]);
      
      toast.success(`Order ${orderNumber} saved locally (offline)`, { duration: 4000 });
      
      // Show print dialog for offline order
      setOrderToPrint({
        ...offlineOrder,
        items: cart,
        subtotal: cartSubtotal,
        packaging_fee: cartPackaging,
        gst_amount: cartTax,
        total_amount: cartTotal,
        customer_name: customerName.trim() || 'Walk-in Customer'
      });
      setShowPrintDialog(true);
      
      setCart([]);
      setCustomerName('');
      setPaymentMethod('cash');
      setLoading(false);
      return;
    }

    // Online order processing
    try {
      const response = await api.post('/orders', orderData);
      
      // Mark as confirmed - Use special POS identifiers to skip Razorpay verification
      await api.post('/payment/verify', {
        razorpay_order_id: 'pos_order',
        razorpay_payment_id: 'pos_payment',
        razorpay_signature: 'pos_signature',
        order_id: response.data.id
      });

      toast.success(`Order ${response.data.order_number} placed successfully!`);
      
      // Show print dialog
      setOrderToPrint({
        ...response.data,
        items: cart,
        subtotal: cartSubtotal,
        packaging_fee: cartPackaging,
        gst_amount: cartTax,
        total_amount: cartTotal,
        customer_name: customerName.trim() || 'Walk-in Customer',
        payment_method: paymentMethod
      });
      setShowPrintDialog(true);
      
      setCart([]);
      setCustomerName('');
      setPaymentMethod('cash');
      fetchOrders();
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  // Print invoice function
  // Bluetooth Thermal Printer State
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [bluetoothConnected, setBluetoothConnected] = useState(false);
  const [printerMode, setPrinterMode] = useState('rawbt'); // 'rawbt' | 'bluetooth' | 'browser'

  // Print via RawBT App (Recommended for Android)
  const printViaRawBT = (order) => {
    // Build ESC/POS commands for 58mm thermal printer
    const ESC = '\x1B';
    const GS = '\x1D';
    
    let receipt = '';
    
    // Initialize & Center
    receipt += ESC + '@'; // Reset
    receipt += ESC + 'a' + '\x01'; // Center align
    
    // Header - Double height
    receipt += ESC + '!' + '\x10';
    receipt += 'FISH ON FIRE\n';
    receipt += ESC + '!' + '\x00'; // Normal
    receipt += (selectedOutlet?.name || 'Main Outlet') + '\n';
    receipt += '--------------------------------\n';
    
    // Order info - Left align
    receipt += ESC + 'a' + '\x00';
    receipt += 'Order: ' + order.order_number + '\n';
    receipt += 'Date: ' + new Date(order.created_at || Date.now()).toLocaleString('en-IN') + '\n';
    receipt += 'Type: ' + (order.order_type === 'dine_in' ? 'Dine In' : order.order_type === 'takeaway' ? 'Takeaway' : order.order_type) + '\n';
    receipt += 'Customer: ' + (order.customer_name || 'Walk-in') + '\n';
    if (order.customer_phone) {
      receipt += 'Phone: ' + order.customer_phone + '\n';
    }
    receipt += '--------------------------------\n';
    
    // Items header
    receipt += 'Item              Qty    Amount\n';
    receipt += '--------------------------------\n';
    
    // Items
    for (const item of (order.items || [])) {
      const name = (item.product_name || 'Item').substring(0, 16).padEnd(16);
      const qty = String(item.quantity).padStart(3);
      const price = ('Rs' + (item.price * item.quantity).toFixed(0)).padStart(10);
      receipt += name + qty + price + '\n';
      
      // Print customizations if any
      if (item.selected_customizations && item.selected_customizations.length > 0) {
        for (const cust of item.selected_customizations) {
          receipt += '  + ' + cust.name + '\n';
        }
      }
      // Print add-ons if any
      if (item.selected_addons && item.selected_addons.length > 0) {
        for (const addon of item.selected_addons) {
          const addonPrice = addon.price > 0 ? (' +Rs' + addon.price) : '';
          receipt += '  + ' + addon.name + addonPrice + '\n';
        }
      }
    }
    
    receipt += '--------------------------------\n';
    
    // Totals
    receipt += ('Subtotal:').padEnd(20) + ('Rs' + (order.subtotal || 0).toFixed(0)).padStart(12) + '\n';
    if ((order.packaging_fee || 0) > 0) {
      receipt += ('Packaging:').padEnd(20) + ('Rs' + order.packaging_fee.toFixed(0)).padStart(12) + '\n';
    }
    if ((order.gst_amount || order.tax_amount || 0) > 0) {
      receipt += ('GST:').padEnd(20) + ('Rs' + (order.gst_amount || order.tax_amount).toFixed(0)).padStart(12) + '\n';
    }
    if ((order.discount_amount || 0) > 0) {
      receipt += ('Discount:').padEnd(20) + ('-Rs' + order.discount_amount.toFixed(0)).padStart(12) + '\n';
    }
    
    // Total - Bold
    receipt += ESC + '!' + '\x10';
    receipt += ('TOTAL:').padEnd(20) + ('Rs' + order.total_amount.toFixed(0)).padStart(12) + '\n';
    receipt += ESC + '!' + '\x00';
    
    // Payment method
    if (order.payment_method) {
      receipt += 'Paid via: ' + order.payment_method.toUpperCase() + '\n';
    }
    
    // Footer
    receipt += '--------------------------------\n';
    receipt += ESC + 'a' + '\x01'; // Center
    receipt += 'Thank you for dining!\n';
    receipt += 'Visit again soon\n';
    receipt += '\n\n\n'; // Feed paper
    
    // Cut paper
    receipt += GS + 'V' + '\x00';
    
    // Encode to base64 for RawBT
    const base64Data = btoa(unescape(encodeURIComponent(receipt)));
    
    // Open RawBT intent
    const rawbtUrl = 'rawbt:base64,' + base64Data;
    
    // Try to open RawBT
    window.location.href = rawbtUrl;
    
    toast.success('Sent to RawBT printer!');
  };

  // Connect to Bluetooth Thermal Printer (58mm/80mm)
  const connectBluetoothPrinter = async () => {
    try {
      if (!navigator.bluetooth) {
        toast.error('Bluetooth not supported on this browser. Use Chrome on Android/Windows.');
        return;
      }

      // Check if Bluetooth is available
      const isAvailable = await navigator.bluetooth.getAvailability();
      if (!isAvailable) {
        toast.error('Bluetooth is not available. Please enable Bluetooth on your device.');
        return;
      }

      toast.info('Opening Bluetooth device picker... Select your printer from the list.');
      
      // Common thermal printer service UUIDs
      const printerServiceUUIDs = [
        '000018f0-0000-1000-8000-00805f9b34fb', // Generic thermal printer
        '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip/ISSC serial
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Serial Port Profile
        '00001101-0000-1000-8000-00805f9b34fb', // SPP UUID
        '0000ff00-0000-1000-8000-00805f9b34fb', // Custom printer service
        '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 BLE module (common in cheap printers)
        '0000fff0-0000-1000-8000-00805f9b34fb', // Another common BLE service
      ];
      
      // Request Bluetooth device - accept all devices to show full list
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: printerServiceUUIDs
      });

      const deviceName = device.name || device.id || 'Unknown Device';
      toast.info(`Connecting to ${deviceName}...`);
      
      const server = await device.gatt.connect();
      setBluetoothDevice({ device, server, name: deviceName });
      setBluetoothConnected(true);
      
      toast.success(`Connected to ${deviceName}!`);
      
      // Listen for disconnection
      device.addEventListener('gattserverdisconnected', () => {
        setBluetoothConnected(false);
        setBluetoothDevice(null);
        toast.warning('Bluetooth printer disconnected');
      });
      
    } catch (error) {
      console.error('Bluetooth connection error:', error);
      if (error.name === 'NotFoundError') {
        toast.error('Pairing cancelled or no device selected.');
      } else if (error.name === 'SecurityError') {
        toast.error('Bluetooth permission denied. Please allow Bluetooth access.');
      } else if (error.name === 'NotSupportedError') {
        toast.error('Bluetooth not supported. Use Chrome browser on Android/Windows.');
      } else {
        toast.error('Connection failed: ' + error.message);
      }
    }
  };

  // Print to Bluetooth Thermal Printer (ESC/POS commands for 58mm)
  const printToBluetoothPrinter = async (order) => {
    if (!bluetoothDevice?.server) {
      toast.error('No Bluetooth printer connected');
      return;
    }

    try {
      // Get the printer service
      const services = await bluetoothDevice.server.getPrimaryServices();
      let characteristic = null;

      for (const service of services) {
        const characteristics = await service.getCharacteristics();
        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            characteristic = char;
            break;
          }
        }
        if (characteristic) break;
      }

      if (!characteristic) {
        toast.error('Could not find printer write characteristic');
        return;
      }

      // ESC/POS commands for 58mm thermal printer
      const ESC = 0x1B;
      const GS = 0x1D;
      const encoder = new TextEncoder();

      // Helper to send data
      const sendData = async (data) => {
        const uint8Array = data instanceof Uint8Array ? data : encoder.encode(data);
        const chunkSize = 100; // Send in small chunks
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.slice(i, i + chunkSize);
          await characteristic.writeValue(chunk);
          await new Promise(r => setTimeout(r, 50)); // Small delay between chunks
        }
      };

      // Initialize printer
      await sendData(new Uint8Array([ESC, 0x40])); // Reset
      await sendData(new Uint8Array([ESC, 0x61, 1])); // Center align

      // Header
      await sendData(new Uint8Array([ESC, 0x21, 0x10])); // Double height
      await sendData('FISH ON FIRE\\n');
      await sendData(new Uint8Array([ESC, 0x21, 0x00])); // Normal
      await sendData((selectedOutlet?.name || 'Main Outlet') + '\\n');
      await sendData('--------------------------------\\n');

      // Order info
      await sendData(new Uint8Array([ESC, 0x61, 0])); // Left align
      await sendData('Order: ' + order.order_number + '\\n');
      await sendData('Date: ' + new Date(order.created_at || Date.now()).toLocaleString('en-IN') + '\\n');
      await sendData('Type: ' + (order.order_type === 'dine_in' ? 'Dine In' : 'Takeaway') + '\\n');
      await sendData('Customer: ' + order.customer_name + '\\n');
      await sendData('--------------------------------\\n');

      // Items
      for (const item of (order.items || [])) {
        const name = item.product_name.substring(0, 18).padEnd(18);
        const qty = String(item.quantity).padStart(2);
        const price = ('₹' + (item.price * item.quantity).toFixed(0)).padStart(8);
        await sendData(name + qty + price + '\\n');
      }

      await sendData('--------------------------------\\n');

      // Totals
      await sendData(('Subtotal:').padEnd(22) + ('₹' + (order.subtotal || 0).toFixed(0)).padStart(10) + '\\n');
      if ((order.gst_amount || 0) > 0) {
        await sendData(('GST:').padEnd(22) + ('₹' + order.gst_amount.toFixed(0)).padStart(10) + '\\n');
      }
      await sendData(new Uint8Array([ESC, 0x21, 0x10])); // Bold
      await sendData(('TOTAL:').padEnd(22) + ('₹' + order.total_amount.toFixed(0)).padStart(10) + '\\n');
      await sendData(new Uint8Array([ESC, 0x21, 0x00])); // Normal

      // Footer
      await sendData('--------------------------------\\n');
      await sendData(new Uint8Array([ESC, 0x61, 1])); // Center
      await sendData('Thank you for dining!\\n');
      await sendData('Visit again soon\\n\\n\\n');

      // Cut paper (if supported)
      await sendData(new Uint8Array([GS, 0x56, 0x00]));

      toast.success('Receipt printed!');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Print failed: ' + error.message);
    }
  };

  // Smart print - uses RawBT (Android), Bluetooth, or browser print
  const smartPrint = async (order) => {
    if (printerMode === 'rawbt') {
      // Use RawBT app (best for Android)
      printViaRawBT(order);
    } else if (printerMode === 'bluetooth' && bluetoothConnected && bluetoothDevice?.server) {
      // Use direct Web Bluetooth printing
      toast.info('Printing to Bluetooth printer...');
      await printToBluetoothPrinter(order);
    } else {
      // Fallback to browser print dialog
      handleBrowserPrint(order);
    }
  };

  // Browser-based print (fallback when no Bluetooth)
  const handleBrowserPrint = (order) => {
    const printContent = `
      <html>
        <head>
          <title>Invoice - ${order.order_number}</title>
          <style>
            body { font-family: 'Courier New', monospace; padding: 20px; max-width: 300px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
            .header h1 { margin: 0; font-size: 18px; }
            .header p { margin: 5px 0; font-size: 12px; }
            .order-info { font-size: 12px; margin-bottom: 10px; }
            .items { border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
            .item { display: flex; justify-content: space-between; font-size: 12px; margin: 5px 0; }
            .item-name { flex: 1; }
            .item-qty { width: 30px; text-align: center; }
            .item-price { width: 60px; text-align: right; }
            .totals { font-size: 12px; }
            .totals .row { display: flex; justify-content: space-between; margin: 3px 0; }
            .totals .total { font-weight: bold; font-size: 14px; border-top: 1px dashed #000; padding-top: 5px; margin-top: 5px; }
            .footer { text-align: center; margin-top: 15px; font-size: 11px; border-top: 2px dashed #000; padding-top: 10px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>FISH ON FIRE</h1>
            <p>${selectedOutlet?.name || 'Main Outlet'}</p>
            <p>${selectedOutlet?.address || ''}</p>
          </div>
          
          <div class="order-info">
            <p><strong>Order #:</strong> ${order.order_number}</p>
            <p><strong>Date:</strong> ${new Date(order.created_at || Date.now()).toLocaleString('en-IN')}</p>
            <p><strong>Type:</strong> ${order.order_type === 'dine_in' ? 'Dine In' : 'Takeaway'}</p>
            <p><strong>Customer:</strong> ${order.customer_name}</p>
          </div>
          
          <div class="items">
            <div class="item" style="font-weight: bold; border-bottom: 1px solid #000; padding-bottom: 3px;">
              <span class="item-name">Item</span>
              <span class="item-qty">Qty</span>
              <span class="item-price">Amount</span>
            </div>
            ${(order.items || []).map(item => `
              <div class="item">
                <span class="item-name">${item.product_name}</span>
                <span class="item-qty">${item.quantity}</span>
                <span class="item-price">₹${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            `).join('')}
          </div>
          
          <div class="totals">
            <div class="row">
              <span>Subtotal:</span>
              <span>₹${(order.subtotal || cartSubtotal).toFixed(2)}</span>
            </div>
            ${(order.packaging_fee || 0) > 0 ? `
              <div class="row">
                <span>Packaging:</span>
                <span>₹${(order.packaging_fee).toFixed(2)}</span>
              </div>
            ` : ''}
            ${(order.gst_amount || 0) > 0 ? `
              <div class="row">
                <span>GST:</span>
                <span>₹${(order.gst_amount).toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="row total">
              <span>TOTAL:</span>
              <span>₹${(order.total_amount).toFixed(2)}</span>
            </div>
          </div>
          
          <div class="footer">
            <p>Thank you for dining with us!</p>
            <p>Visit again soon</p>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Main print handler - always use this
  const handlePrintInvoice = (order) => {
    smartPrint(order);
  };

  // Print invoice for existing order
  const handlePrintExistingOrder = (order) => {
    handlePrintInvoice({
      ...order,
      items: order.items || [],
      subtotal: order.subtotal || order.total_amount,
      packaging_fee: order.packaging_fee || 0,
      gst_amount: order.gst_amount || order.tax_amount || 0
    });
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  // Open edit order dialog
  const openEditOrderDialog = (order) => {
    setEditingOrder(order);
    setEditOrderItems(order.items.map(item => ({...item})));
    setEditCustomerName(order.customer_name || '');
    setEditOrderType(order.order_type || 'dine_in');
    setEditPaymentMethod(order.payment_method || 'cash');
    setShowEditOrderDialog(true);
  };

  // Update item quantity in edit dialog
  const updateEditItemQuantity = (index, delta) => {
    setEditOrderItems(prev => {
      const newItems = [...prev];
      const newQty = Math.max(0, newItems[index].quantity + delta);
      if (newQty === 0) {
        // Remove item if quantity becomes 0
        return newItems.filter((_, i) => i !== index);
      }
      newItems[index] = {...newItems[index], quantity: newQty};
      return newItems;
    });
  };

  // Remove item from edit dialog
  const removeEditItem = (index) => {
    setEditOrderItems(prev => prev.filter((_, i) => i !== index));
  };

  // Calculate edit order total
  const calculateEditTotal = () => {
    const subtotal = editOrderItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const gstRate = editingOrder?.gst_rate || 5;
    const gst = subtotal * (gstRate / 100);
    return {
      subtotal: subtotal.toFixed(2),
      gst: gst.toFixed(2),
      total: (subtotal + gst).toFixed(2)
    };
  };

  // Save edited order
  const saveEditedOrder = async () => {
    if (!editingOrder) return;
    if (editOrderItems.length === 0) {
      toast.error('Order must have at least one item');
      return;
    }
    
    setEditingLoading(true);
    try {
      await api.put(`/orders/${editingOrder.id}/edit`, {
        items: editOrderItems,
        customer_name: editCustomerName,
        order_type: editOrderType,
        payment_method: editPaymentMethod
      });
      toast.success('Order updated successfully');
      setShowEditOrderDialog(false);
      setEditingOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setEditingLoading(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = (order) => {
    setDeletingOrder(order);
    setShowDeleteConfirm(true);
  };

  // Delete order
  const deleteOrder = async () => {
    if (!deletingOrder) return;
    
    setDeletingLoading(true);
    try {
      await api.delete(`/orders/${deletingOrder.id}?restore_stock=true`);
      toast.success('Order deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingOrder(null);
      setShowOrderDetails(false);
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeletingLoading(false);
    }
  };

  // Toggle product availability
  const toggleProductAvailability = async (productId, currentStatus) => {
    try {
      await api.put(`/products/${productId}`, { available: !currentStatus });
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === productId ? {...p, available: !currentStatus} : p
      ));
      toast.success(`Product ${!currentStatus ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling product:', error);
      toast.error('Failed to update product');
    }
  };

  // All products for management view (including disabled)
  const allProductsFiltered = products
    .filter(p => selectedCategory === 'All' || p.category === selectedCategory)
    .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  // POS shows ALL products (including disabled) - staff need to see everything
  // Disabled products will show with a visual indicator
  const filteredProducts = products
    .filter(p => selectedCategory === 'All' || p.category === selectedCategory)
    .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  // Fetch today's sales summary
  const fetchTodaySummary = async () => {
    if (!selectedOutlet) return;
    setLoadingTodaySummary(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get(`/pos/today-summary?outlet_id=${selectedOutlet}&date=${today}`);
      setTodaySummary(response.data);
    } catch (error) {
      console.error('Error fetching today summary:', error);
      toast.error('Failed to load today\'s summary');
    } finally {
      setLoadingTodaySummary(false);
    }
  };

  // Fetch reconciliation data (current stock for all products)
  const fetchReconciliationData = async () => {
    if (!selectedOutlet) return;
    setReconciliationLoading(true);
    try {
      const response = await api.get(`/pos/reconciliation?outlet_id=${selectedOutlet}`);
      setReconciliationData(response.data.products.map(p => ({
        ...p,
        physical_count: p.system_stock // Default to system stock
      })));
    } catch (error) {
      console.error('Error fetching reconciliation data:', error);
      toast.error('Failed to load stock data');
    } finally {
      setReconciliationLoading(false);
    }
  };

  // Update physical count in reconciliation
  const updatePhysicalCount = (productId, count) => {
    setReconciliationData(prev => prev.map(p => 
      p.product_id === productId ? {...p, physical_count: parseInt(count) || 0} : p
    ));
  };

  // Save reconciliation
  const saveReconciliation = async () => {
    if (!selectedOutlet) return;
    setSavingReconciliation(true);
    try {
      const entries = reconciliationData
        .filter(p => p.physical_count !== p.system_stock)
        .map(p => ({
          product_id: p.product_id,
          product_name: p.product_name,
          system_stock: p.system_stock,
          physical_stock: p.physical_count,
          difference: p.physical_count - p.system_stock
        }));
      
      if (entries.length === 0) {
        toast.info('No differences to reconcile');
        setShowReconciliation(false);
        return;
      }
      
      await api.post('/pos/reconciliation', {
        outlet_id: selectedOutlet,
        entries,
        reconciled_by: 'POS Staff'
      });
      
      toast.success(`Reconciliation saved! ${entries.length} item(s) updated`);
      setShowReconciliation(false);
      fetchProducts(); // Refresh products with new stock
    } catch (error) {
      console.error('Error saving reconciliation:', error);
      toast.error('Failed to save reconciliation');
    } finally {
      setSavingReconciliation(false);
    }
  };

  // Check if today is Sunday (reconciliation day)
  const isSunday = new Date().getDay() === 0;

  return (
    <div className='min-h-screen bg-[#050505]'>
      {/* Audio element for notifications */}
      <audio ref={audioRef} src="/notification.wav" preload="auto" />
      
      {/* Header */}
      <header className='bg-[#121212] border-b border-white/10'>
        <div className='container mx-auto px-2 sm:px-4 py-3 sm:py-4'>
          {/* Mobile: Stack vertically, Desktop: Flex horizontal */}
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
            {/* Logo row */}
            <div className='flex items-center justify-between sm:justify-start gap-2 sm:gap-4'>
              <div className='flex items-center gap-2 sm:gap-4'>
                <Link to='/'>
                  <Button variant='ghost' className='text-white px-2 sm:px-4 h-8 sm:h-10 text-sm sm:text-base'>← Back</Button>
                </Link>
                <Monitor className='w-6 h-6 sm:w-8 sm:h-8 text-[#00E5FF]' strokeWidth={1.5} />
                <h1 className='text-xl sm:text-3xl font-bold text-white' style={{ fontFamily: 'Bebas Neue' }}>
                  POS System
                </h1>
              </div>
              {/* Mobile: Show logout here */}
              <Button
                onClick={() => {
                  logout('pos');
                  navigate('/');
                }}
                variant='outline'
                size='sm'
                className='sm:hidden border-white/20 text-white hover:bg-red-500/10 hover:border-red-500/50'
              >
                <LogOut className='w-4 h-4' />
              </Button>
            </div>
            
            {/* Action buttons - scrollable on mobile */}
            <div className='flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-hide'>
              <Button 
                onClick={() => setShowManageProducts(!showManageProducts)}
                size='sm'
                variant={showManageProducts ? 'default' : 'outline'}
                className={`whitespace-nowrap text-xs sm:text-sm ${showManageProducts ? 'bg-[#FFD600] text-black hover:bg-[#FFD600]/90' : 'border-white/20 text-white hover:bg-white/5'}`}
                data-testid='manage-products'
              >
                {showManageProducts ? 'Back' : 'Manage Products'}
              </Button>
              <Button 
                onClick={() => setShowStockAlerts(true)}
                size='sm'
                variant='outline'
                className='border-white/20 text-white hover:bg-white/5 relative whitespace-nowrap text-xs sm:text-sm'
                data-testid='stock-alerts'
              >
                <AlertTriangle className='w-4 h-4 mr-1 sm:mr-2' />
                <span className='hidden xs:inline'>Stock Alerts</span>
                <span className='xs:hidden'>Alerts</span>
                {criticalStockAlerts.length > 0 && (
                  <span className='absolute -top-2 -right-2 bg-yellow-500 text-black text-xs rounded-full w-5 h-5 flex items-center justify-center'>
                    {criticalStockAlerts.length}
                  </span>
                )}
              </Button>
              <Button 
                onClick={() => setShowOrders(true)}
                size='sm'
                variant='outline'
                className='border-white/20 text-white hover:bg-white/5 relative whitespace-nowrap text-xs sm:text-sm'
                data-testid='view-orders'
              >
                <span className='hidden xs:inline'>View Orders</span>
                <span className='xs:hidden'>Orders</span>
                {newOrderCount > 0 && (
                  <span className='absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse'>
                    {newOrderCount}
                  </span>
                )}
              </Button>
              
              {/* Sound toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSoundEnabled(!soundEnabled);
                  toast.info(soundEnabled ? 'Sound alerts disabled' : 'Sound alerts enabled');
                }}
                className={`border-white/20 p-2 ${soundEnabled ? 'text-green-400 border-green-400/50' : 'text-gray-500'}`}
                title={soundEnabled ? 'Sound On' : 'Sound Off'}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </Button>
              
              {/* Push Notification toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={notificationsEnabled ? () => {
                  setNotificationsEnabled(false);
                  toast.info('Push notifications disabled');
                } : enablePushNotifications}
                className={`border-white/20 p-2 ${notificationsEnabled ? 'text-green-400 border-green-400/50' : 'text-gray-500'}`}
                title={notificationsEnabled ? 'Notifications On' : 'Enable Notifications'}
              >
                {notificationsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </Button>
              
              {/* Today's Summary Button */}
              <Button
                onClick={() => { setShowTodaySummary(true); fetchTodaySummary(); }}
                size='sm'
                variant='outline'
                className='border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs sm:text-sm'
                disabled={!selectedOutlet}
                title="Today's Sales Summary"
              >
                <BarChart3 className='w-4 h-4 sm:mr-1' />
                <span className='hidden sm:inline'>Today</span>
              </Button>
              
              {/* Reconciliation Button - Highlighted on Sunday */}
              <Button
                onClick={() => { setShowReconciliation(true); fetchReconciliationData(); }}
                size='sm'
                variant='outline'
                className={`text-xs sm:text-sm ${isSunday 
                  ? 'border-yellow-500 text-yellow-400 hover:bg-yellow-500/10 animate-pulse' 
                  : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10'}`}
                disabled={!selectedOutlet}
                title="Stock Reconciliation"
              >
                <ClipboardCheck className='w-4 h-4 sm:mr-1' />
                <span className='hidden sm:inline'>Recon</span>
              </Button>
              
              {/* Quick Wastage Entry */}
              <Button
                onClick={() => setShowQuickWastage(true)}
                size='sm'
                variant='outline'
                className='border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs sm:text-sm'
                disabled={!selectedOutlet}
                title="Record Wastage/Damaged"
              >
                <Trash2 className='w-4 h-4 sm:mr-1' />
                <span className='hidden sm:inline'>Wastage</span>
              </Button>
              
              {/* Desktop logout */}
              <Button
                onClick={() => {
                  logout('pos');
                  navigate('/');
                }}
                variant='outline'
                size='sm'
                className='hidden sm:flex border-white/20 text-white hover:bg-red-500/10 hover:border-red-500/50'
              >
                <LogOut className='w-4 h-4 mr-2' />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className='container mx-auto px-4 py-6'>
        {/* Outlet Selector - Show if no outlet selected or multiple outlets */}
        {!selectedOutlet ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <Store className="w-16 h-16 text-[#00E5FF] mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Select Outlet</h2>
            <p className="text-gray-400 mb-6">Choose an outlet to manage orders</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-lg">
              {outlets.map(outlet => (
                <Card
                  key={outlet.id}
                  onClick={() => {
                    setSelectedOutlet(outlet);
                    setIsAcceptingOrders(outlet.is_accepting_orders !== false);
                    setIsAcceptingDineIn(outlet.is_accepting_dine_in !== false);
                    setIsAcceptingDelivery(outlet.is_accepting_delivery !== false);
                  }}
                  className="cursor-pointer bg-[#121212] border-white/10 hover:border-[#00E5FF] transition-colors p-4"
                >
                  <h3 className="text-white font-semibold">{outlet.name}</h3>
                  <p className="text-gray-400 text-sm">{outlet.address}</p>
                  <Badge className={`mt-2 ${outlet.is_accepting_orders !== false ? 'bg-green-500' : 'bg-red-500'}`}>
                    {outlet.is_accepting_orders !== false ? 'Accepting Orders' : 'Not Accepting'}
                  </Badge>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Outlet Info Bar */}
            <div className="flex items-center justify-between mb-4 p-3 bg-[#121212] rounded-lg border border-white/10">
              <div className="flex items-center gap-3">
                <Store className="w-5 h-5 text-[#00E5FF]" />
                <div>
                  <p className="text-white font-medium">{selectedOutlet.name}</p>
                  <p className="text-gray-400 text-xs">{selectedOutlet.address}</p>
                </div>
                {outlets.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedOutlet(null)}
                    className="ml-2 border-white/20 text-white text-xs"
                  >
                    Change
                  </Button>
                )}
              </div>
              
              {/* Order Toggle Buttons */}
              <div className="flex items-center gap-2">
                {/* Dine-in Toggle */}
                <Button
                  onClick={toggleDineInAcceptance}
                  size="sm"
                  className={`${isAcceptingDineIn 
                    ? 'bg-purple-500 hover:bg-purple-600' 
                    : 'bg-gray-600 hover:bg-gray-700'} text-white`}
                  data-testid="toggle-dine-in"
                >
                  <UtensilsCrossed className="w-4 h-4 mr-1" />
                  {isAcceptingDineIn ? 'Dine-in ON' : 'Dine-in OFF'}
                </Button>
                
                {/* Delivery Toggle */}
                <Button
                  onClick={toggleDeliveryAcceptance}
                  size="sm"
                  className={`${isAcceptingDelivery 
                    ? 'bg-blue-500 hover:bg-blue-600' 
                    : 'bg-gray-600 hover:bg-gray-700'} text-white`}
                  data-testid="toggle-delivery"
                >
                  <Truck className="w-4 h-4 mr-1" />
                  {isAcceptingDelivery ? 'Delivery ON' : 'Delivery OFF'}
                </Button>
                
                {/* Main Order Toggle */}
                <Button
                  onClick={toggleOrderAcceptance}
                  className={`${isAcceptingOrders 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-red-500 hover:bg-red-600'} text-white`}
                  data-testid="toggle-orders"
                >
                  <Power className="w-4 h-4 mr-2" />
                  {isAcceptingOrders ? 'Taking Orders' : 'Orders Off'}
                </Button>
                
                {/* Printer Mode Selector */}
                <div className="flex items-center gap-1">
                  <select
                    value={printerMode}
                    onChange={(e) => {
                      setPrinterMode(e.target.value);
                      if (e.target.value !== 'bluetooth') {
                        setBluetoothDevice(null);
                        setBluetoothConnected(false);
                      }
                      toast.success(`Printer: ${e.target.value === 'rawbt' ? 'RawBT App' : e.target.value === 'bluetooth' ? 'Web Bluetooth' : 'Browser Print'}`);
                    }}
                    className="bg-zinc-800 border border-white/20 text-white text-sm rounded-lg px-3 py-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="rawbt">🖨️ RawBT</option>
                    <option value="bluetooth">📶 BT Direct</option>
                    <option value="browser">🌐 Browser</option>
                  </select>
                  
                  {printerMode === 'bluetooth' && (
                    <Button
                      onClick={bluetoothConnected ? () => { setBluetoothDevice(null); setBluetoothConnected(false); } : connectBluetoothPrinter}
                      variant="outline"
                      size="sm"
                      className={`${bluetoothConnected 
                        ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' 
                        : 'border-white/20 text-gray-400 hover:bg-white/5'}`}
                    >
                      {bluetoothConnected ? '✓ Connected' : 'Connect'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Offline Mode Banner */}
            {isOffline && (
              <div className='bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-4 flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <WifiOff className='w-5 h-5 text-yellow-400' />
                  <div>
                    <p className='text-yellow-400 font-medium'>Offline Mode</p>
                    <p className='text-yellow-400/70 text-sm'>Orders will be saved locally and synced when back online</p>
                  </div>
                </div>
                {offlineOrders.length > 0 && (
                  <Badge className='bg-yellow-500 text-black'>
                    {offlineOrders.length} pending
                  </Badge>
                )}
              </div>
            )}

            {/* Sync Banner - when online and has offline orders */}
            {!isOffline && offlineOrders.length > 0 && (
              <div className='bg-blue-500/20 border border-blue-500/50 rounded-lg p-3 mb-4 flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <Wifi className='w-5 h-5 text-blue-400' />
                  <div>
                    <p className='text-blue-400 font-medium'>{offlineOrders.length} Offline Order(s) Ready to Sync</p>
                    <p className='text-blue-400/70 text-sm'>Click sync to upload orders to server</p>
                  </div>
                </div>
                <Button
                  onClick={syncOfflineOrders}
                  disabled={syncingOrders}
                  className='bg-blue-500 hover:bg-blue-600 text-white'
                >
                  {syncingOrders ? (
                    <>
                      <RefreshCw className='w-4 h-4 mr-2 animate-spin' />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className='w-4 h-4 mr-2' />
                      Sync Now
                    </>
                  )}
                </Button>
              </div>
            )}

            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
              {/* Products Section */}
              <div className='lg:col-span-2'>
                {/* Search and Category */}
                <div className='mb-4 space-y-4'>
                  <div className='relative'>
                    <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                    <Input 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder='Search products...'
                      className='bg-[#121212] border-white/10 text-white pl-10'
                      data-testid='product-search'
                    />
                  </div>
                  
                  <div className='flex gap-2 overflow-x-auto pb-2'>
                    {categories.map(cat => (
                      <Button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        variant={selectedCategory === cat ? 'default' : 'outline'}
                        className={selectedCategory === cat 
                          ? 'bg-[#00E5FF] text-black hover:bg-[#00B8D4]' 
                          : 'border-white/20 text-white hover:bg-white/5'
                        }
                        data-testid={`pos-category-${cat.toLowerCase()}`}
                      >
                        {cat}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* MANAGE PRODUCTS VIEW */}
                {showManageProducts ? (
                  <div className='space-y-3'>
                    <div className='flex justify-between items-center mb-4'>
                      <div>
                        <h2 className='text-white text-lg font-semibold'>Manage Product Availability</h2>
                        <p className='text-gray-400 text-sm'>Toggle products on/off to show or hide them from POS and customer app</p>
                      </div>
                      <Button
                        onClick={() => setShowWastageHistory(true)}
                        variant='outline'
                        className='border-red-500/30 text-red-400 hover:bg-red-500/10'
                        data-testid='view-wastage-history'
                      >
                        <Trash2 className='w-4 h-4 mr-2' />
                        Wastage History
                      </Button>
                    </div>
                    {allProductsFiltered.map(product => (
                      <Card key={product.id} className={`bg-[#121212] border-white/10 ${product.available === false ? 'opacity-60' : ''}`}>
                        <CardContent className='p-3'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                              <div className='w-12 h-12 rounded-lg overflow-hidden bg-black/20'>
                                <img 
                                  src={getImageUrl(product.image)} 
                                  alt={product.name}
                                  className='w-full h-full object-cover'
                                />
                              </div>
                              <div>
                                <h3 className='text-white font-medium'>{product.name}</h3>
                                <p className='text-gray-400 text-sm'>₹{product.price}</p>
                                <p className={`text-xs ${product.stock > 10 ? 'text-green-400' : product.stock > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                                  {product.stock > 0 ? `Stock: ${product.stock}` : 'Out of Stock'}
                                </p>
                              </div>
                            </div>
                            <div className='flex items-center gap-2'>
                              {/* Log Wastage Button */}
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() => openWastageDialog(product)}
                                className='border-red-500/30 text-red-400 hover:bg-red-500/10'
                                disabled={!product.stock || product.stock <= 0}
                                data-testid={`wastage-${product.id}`}
                              >
                                <Trash2 className='w-3 h-3 mr-1' />
                                Waste
                              </Button>
                              <span className={`text-sm ${product.available !== false ? 'text-green-400' : 'text-red-400'}`}>
                                {product.available !== false ? 'Available' : 'Unavailable'}
                              </span>
                              <Switch
                                checked={product.available !== false}
                                onCheckedChange={() => toggleProductAvailability(product.id, product.available !== false)}
                                data-testid={`toggle-product-${product.id}`}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  /* NORMAL POS PRODUCTS VIEW - Shows ALL products including disabled */
                  <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3'>
                    {filteredProducts.map(product => (
                      <Card
                        key={product.id}
                        onClick={() => openCustomizationDialog(product)}
                        className={`cursor-pointer bg-[#121212] border-white/10 hover:border-[#00E5FF]/50 transition-colors ${product.available === false ? 'opacity-50 border-red-500/30' : ''}`}
                        data-testid={`pos-product-${product.id}`}
                      >
                        <CardContent className='p-3 relative'>
                          {/* Disabled indicator */}
                          {product.available === false && (
                            <div className='absolute top-2 right-2 z-10'>
                              <Badge className='bg-red-500/80 text-white text-xs'>
                                Disabled
                              </Badge>
                            </div>
                          )}
                          {/* Add-on Only indicator */}
                          {product.is_addon_only && (
                            <div className='absolute top-2 right-2 z-10'>
                              <Badge className='bg-yellow-500/80 text-black text-xs'>
                                Add-on Only
                              </Badge>
                            </div>
                          )}
                          {/* Add-ons indicator - use effective fields if available */}
                          {(((product.effective_addons && product.effective_addons.length > 0) || (product.addons && product.addons.length > 0)) || 
                            ((product.effective_customization_groups && product.effective_customization_groups.length > 0) || (product.customization_groups && product.customization_groups.length > 0))) && (
                            <div className='absolute top-2 left-2 z-10'>
                              <Badge className='bg-[#FF4500]/80 text-white text-xs'>
                                🔥 Customize
                              </Badge>
                            </div>
                          )}
                          <div className='aspect-square mb-2 rounded-lg overflow-hidden bg-black/20'>
                            <img 
                              src={getImageUrl(product.image)} 
                              alt={product.name}
                              className='w-full h-full object-cover'
                            />
                          </div>
                          <h3 className='text-sm font-bold text-white line-clamp-1 mb-1'>
                            {product.name}
                          </h3>
                          <p className='text-[#00E5FF] font-bold'>
                            {formatCurrency(product.price)}
                          </p>
                        {product.stock < 10 && product.stock > 0 && (
                          <Badge className='mt-1 text-xs bg-yellow-500/20 text-yellow-400'>
                            Only {product.stock} left
                          </Badge>
                        )}
                        {product.stock === 0 && (
                          <Badge className='mt-1 text-xs bg-red-500/20 text-red-400'>
                            Out of Stock
                          </Badge>
                        )}
                        {product.stock >= 10 && (
                          <Badge className='mt-1 text-xs bg-green-500/20 text-green-400'>
                            {product.stock} in stock
                          </Badge>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  </div>
                )}
              </div>

              {/* Cart Section - Desktop */}
              <div className='lg:col-span-1 hidden lg:block'>
            <Card className='bg-[#121212] border-white/10 sticky top-6'>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <CardTitle className='text-2xl text-white flex items-center gap-2' style={{ fontFamily: 'Bebas Neue' }}>
                    <ShoppingCart className='w-6 h-6' />
                    Current Order
                  </CardTitle>
                  {activeOffers.length > 0 && (
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setShowOffersPanel(true)}
                      className={`border-[#FF4500]/50 text-[#FF4500] hover:bg-[#FF4500]/10 ${eligibleOffers.length > 0 ? 'animate-pulse' : ''}`}
                    >
                      <Gift className='w-4 h-4 mr-1' />
                      Offers
                      {eligibleOffers.length > 0 && (
                        <Badge className='ml-1 bg-green-500 text-white text-xs px-1'>{eligibleOffers.length}</Badge>
                      )}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className='space-y-4'>
                {/* Order Type */}
                <div>
                  <Label className='text-white mb-2 block'>Order Type</Label>
                  <RadioGroup value={orderType} onValueChange={setOrderType}>
                    <div className='flex items-center space-x-2 bg-white/5 p-2 rounded-lg'>
                      <RadioGroupItem value='dine_in' id='dine_in' />
                      <Label htmlFor='dine_in' className='flex-1 cursor-pointer text-white'>Dine In</Label>
                    </div>
                    <div className='flex items-center space-x-2 bg-white/5 p-2 rounded-lg'>
                      <RadioGroupItem value='takeaway' id='takeaway' />
                      <Label htmlFor='takeaway' className='flex-1 cursor-pointer text-white'>Takeaway</Label>
                      {orderType === 'takeaway' && (
                        <Badge className='bg-yellow-500/20 text-yellow-400 text-xs'>+ Packaging</Badge>
                      )}
                    </div>
                  </RadioGroup>
                </div>

                {/* Customer Name (Optional) */}
                <div>
                  <Label className='text-white mb-2 block text-sm'>Customer Name (Optional)</Label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder='Walk-in Customer'
                    className='bg-white/5 border-white/10 text-white'
                  />
                </div>

                {/* Cart Items */}
                <div className='space-y-2 max-h-60 overflow-y-auto'>
                  {cart.length === 0 ? (
                    <p className='text-gray-400 text-center py-8'>No items in cart</p>
                  ) : (
                    cart.map(item => (
                      <div key={item.cart_key || item.product_id} className='bg-white/5 rounded-lg p-3'>
                        <div className='flex items-start justify-between mb-1'>
                          <div className='flex-1'>
                            <h4 className='text-white font-semibold text-sm'>{item.product_name}</h4>
                            {/* Show selected add-ons */}
                            {item.selected_addons && item.selected_addons.length > 0 && (
                              <div className='flex flex-wrap gap-1 mt-1'>
                                {item.selected_addons.map((addon, idx) => (
                                  <span key={idx} className='text-xs bg-[#FF4500]/20 text-[#FF4500] px-1.5 py-0.5 rounded'>
                                    +{addon.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* Show selected customizations */}
                            {item.selected_customizations && item.selected_customizations.length > 0 && (
                              <div className='flex flex-wrap gap-1 mt-1'>
                                {item.selected_customizations.map((cust, idx) => (
                                  <span key={idx} className='text-xs bg-[#00E5FF]/20 text-[#00E5FF] px-1.5 py-0.5 rounded'>
                                    {cust.option}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* Show item notes */}
                            {item.item_notes && (
                              <p className='text-xs text-gray-400 mt-1'>📝 {item.item_notes}</p>
                            )}
                          </div>
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => removeFromCart(item.cart_key || item.product_id)}
                            className='text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 w-6 p-0'
                          >
                            <X className='w-4 h-4' />
                          </Button>
                        </div>
                        <div className='flex items-center justify-between'>
                          <div className='flex items-center gap-2'>
                            <Button
                              size='sm'
                              onClick={() => updateQuantity(item.cart_key || item.product_id, -1)}
                              className='h-7 w-7 p-0 bg-white/10 hover:bg-white/20'
                            >
                              <Minus className='w-3 h-3' />
                            </Button>
                            <span className='w-8 text-center font-mono text-white'>{item.quantity}</span>
                            <Button
                              size='sm'
                              onClick={() => updateQuantity(item.cart_key || item.product_id, 1)}
                              className='h-7 w-7 p-0 bg-white/10 hover:bg-white/20'
                            >
                              <Plus className='w-3 h-3' />
                            </Button>
                          </div>
                          <span className='text-[#00E5FF] font-bold'>
                            {formatCurrency(item.price * item.quantity)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Bill Summary */}
                <div className='border-t border-white/10 pt-4 space-y-2'>
                  <div className='flex items-center justify-between text-sm'>
                    <span className='text-gray-400'>Subtotal</span>
                    <span className='text-white'>{formatCurrency(cartSubtotal)}</span>
                  </div>
                  {cartPackaging > 0 && (
                    <div className='flex items-center justify-between text-sm'>
                      <span className='text-gray-400'>Packaging</span>
                      <span className='text-yellow-400'>+{formatCurrency(cartPackaging)}</span>
                    </div>
                  )}
                  {cartTax > 0 && (
                    <div className='flex items-center justify-between text-sm'>
                      <span className='text-gray-400'>GST</span>
                      <span className='text-white'>+{formatCurrency(cartTax)}</span>
                    </div>
                  )}
                  <div className='flex items-center justify-between pt-2 border-t border-white/10'>
                    <span className='text-xl font-bold text-white'>Total</span>
                    <span className='text-2xl font-bold text-[#00E5FF]' style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(cartTotal)}
                    </span>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className='border-t border-white/10 pt-4'>
                  <Label className='text-gray-400 text-sm mb-2 block'>Payment Method</Label>
                  <div className='grid grid-cols-3 gap-2'>
                    <Button
                      variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                      onClick={() => setPaymentMethod('cash')}
                      className={`flex flex-col items-center py-3 h-auto ${
                        paymentMethod === 'cash' 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'border-white/20 text-white hover:bg-white/5'
                      }`}
                      data-testid='payment-cash-desktop'
                    >
                      <Banknote className='w-5 h-5 mb-1' />
                      <span className='text-xs'>Cash</span>
                    </Button>
                    <Button
                      variant={paymentMethod === 'upi' ? 'default' : 'outline'}
                      onClick={() => setPaymentMethod('upi')}
                      disabled={isOffline}
                      className={`flex flex-col items-center py-3 h-auto ${
                        paymentMethod === 'upi' 
                          ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                          : 'border-white/20 text-white hover:bg-white/5'
                      } ${isOffline ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid='payment-upi-desktop'
                    >
                      <Smartphone className='w-5 h-5 mb-1' />
                      <span className='text-xs'>UPI</span>
                    </Button>
                    <Button
                      variant={paymentMethod === 'card' ? 'default' : 'outline'}
                      onClick={() => setPaymentMethod('card')}
                      disabled={isOffline}
                      className={`flex flex-col items-center py-3 h-auto ${
                        paymentMethod === 'card' 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'border-white/20 text-white hover:bg-white/5'
                      } ${isOffline ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid='payment-card-desktop'
                    >
                      <CreditCard className='w-5 h-5 mb-1' />
                      <span className='text-xs'>Card</span>
                    </Button>
                  </div>
                  {isOffline && (
                    <p className='text-yellow-400 text-xs mt-2'>⚠️ Only cash available offline</p>
                  )}
                </div>

                <div className='space-y-2'>
                    <Button
                      onClick={handleCheckout}
                      disabled={loading || cart.length === 0}
                      className='w-full bg-[#00C853] hover:bg-[#00E676] text-white py-6 text-lg font-bold'
                      data-testid='pos-checkout'
                    >
                      Complete Order
                    </Button>
                    <Button
                      onClick={() => setCart([])}
                      variant='outline'
                      disabled={cart.length === 0}
                      className='w-full border-white/20 text-white hover:bg-white/5'
                    >
                      Clear Cart
                    </Button>
                  </div>
              </CardContent>
            </Card>
          </div>
        </div>

          {/* Mobile Cart Floating Button */}
          <div className='lg:hidden fixed bottom-4 left-4 right-4 z-50'>
            <Button
              onClick={() => setShowMobileCart(true)}
              className='w-full bg-[#00E5FF] hover:bg-[#00B8D4] text-black py-6 rounded-xl shadow-lg flex items-center justify-between px-6'
              data-testid='mobile-cart-btn'
            >
              <div className='flex items-center gap-3'>
                <div className='bg-black/20 rounded-full p-2'>
                  <ShoppingCart className='w-5 h-5' />
                </div>
                <div className='text-left'>
                  <p className='text-sm font-bold'>{cartItemCount} items</p>
                  <p className='text-xs opacity-80'>{orderType === 'dine_in' ? 'Dine In' : 'Takeaway'}</p>
                </div>
              </div>
              <div className='text-right'>
                <p className='text-xl font-bold' style={{ fontFamily: 'JetBrains Mono' }}>{formatCurrency(cartTotal)}</p>
                <p className='text-xs opacity-80'>View Cart →</p>
              </div>
            </Button>
          </div>

          {/* Mobile Cart Dialog */}
          <Dialog open={showMobileCart} onOpenChange={setShowMobileCart}>
            <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto'>
              <DialogHeader>
                <DialogTitle className='text-2xl font-bold flex items-center gap-2' style={{ fontFamily: 'Bebas Neue' }}>
                  <ShoppingCart className='w-6 h-6' />
                  Current Order
                </DialogTitle>
              </DialogHeader>
              
              <div className='space-y-4'>
                {/* Order Type */}
                <div>
                  <Label className='text-white mb-2 block'>Order Type</Label>
                  <RadioGroup value={orderType} onValueChange={setOrderType}>
                    <div className='flex gap-2'>
                      <div className={`flex-1 flex items-center space-x-2 p-3 rounded-lg cursor-pointer ${orderType === 'dine_in' ? 'bg-[#00E5FF]/20 border border-[#00E5FF]' : 'bg-white/5'}`}
                        onClick={() => setOrderType('dine_in')}>
                        <RadioGroupItem value='dine_in' id='mobile_dine_in' />
                        <Label htmlFor='mobile_dine_in' className='cursor-pointer text-white'>Dine In</Label>
                      </div>
                      <div className={`flex-1 flex items-center space-x-2 p-3 rounded-lg cursor-pointer ${orderType === 'takeaway' ? 'bg-[#00E5FF]/20 border border-[#00E5FF]' : 'bg-white/5'}`}
                        onClick={() => setOrderType('takeaway')}>
                        <RadioGroupItem value='takeaway' id='mobile_takeaway' />
                        <Label htmlFor='mobile_takeaway' className='cursor-pointer text-white'>Takeaway</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* Customer Name */}
                <div>
                  <Label className='text-white mb-2 block text-sm'>Customer Name (Optional)</Label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder='Walk-in Customer'
                    className='bg-white/5 border-white/10 text-white'
                  />
                </div>

                {/* Cart Items */}
                <div className='space-y-2 max-h-48 overflow-y-auto'>
                  {cart.length === 0 ? (
                    <p className='text-gray-400 text-center py-4'>No items in cart</p>
                  ) : (
                    cart.map(item => (
                      <div key={item.cart_key || item.product_id} className='bg-white/5 rounded-lg p-3'>
                        <div className='flex items-center justify-between'>
                          <div className='flex-1'>
                            <h4 className='text-white font-semibold text-sm'>{item.product_name}</h4>
                            {/* Show selected add-ons */}
                            {item.selected_addons && item.selected_addons.length > 0 && (
                              <div className='flex flex-wrap gap-1 mt-0.5'>
                                {item.selected_addons.map((addon, idx) => (
                                  <span key={idx} className='text-xs bg-[#FF4500]/20 text-[#FF4500] px-1 py-0.5 rounded'>
                                    +{addon.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* Show selected customizations */}
                            {item.selected_customizations && item.selected_customizations.length > 0 && (
                              <div className='flex flex-wrap gap-1 mt-0.5'>
                                {item.selected_customizations.map((cust, idx) => (
                                  <span key={idx} className='text-xs bg-[#00E5FF]/20 text-[#00E5FF] px-1 py-0.5 rounded'>
                                    {cust.option}
                                  </span>
                                ))}
                              </div>
                            )}
                            <p className='text-[#00E5FF] text-sm'>{formatCurrency(item.price)} × {item.quantity}</p>
                          </div>
                          <div className='flex items-center gap-2'>
                            <Button size='sm' onClick={() => updateQuantity(item.cart_key || item.product_id, -1)} className='h-7 w-7 p-0 bg-white/10'>
                              <Minus className='w-3 h-3' />
                            </Button>
                            <span className='w-6 text-center text-white'>{item.quantity}</span>
                            <Button size='sm' onClick={() => updateQuantity(item.cart_key || item.product_id, 1)} className='h-7 w-7 p-0 bg-white/10'>
                              <Plus className='w-3 h-3' />
                            </Button>
                            <Button size='sm' variant='ghost' onClick={() => removeFromCart(item.cart_key || item.product_id)} className='h-7 w-7 p-0 text-red-400'>
                              <X className='w-4 h-4' />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Bill Summary */}
                <div className='border-t border-white/10 pt-4 space-y-2'>
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-400'>Subtotal</span>
                    <span className='text-white'>{formatCurrency(cartSubtotal)}</span>
                  </div>
                  {cartPackaging > 0 && (
                    <div className='flex justify-between text-sm'>
                      <span className='text-gray-400'>Packaging</span>
                      <span className='text-yellow-400'>+{formatCurrency(cartPackaging)}</span>
                    </div>
                  )}
                  {cartTax > 0 && (
                    <div className='flex justify-between text-sm'>
                      <span className='text-gray-400'>GST</span>
                      <span className='text-white'>+{formatCurrency(cartTax)}</span>
                    </div>
                  )}
                  <div className='flex justify-between pt-2 border-t border-white/10'>
                    <span className='text-lg font-bold text-white'>Total</span>
                    <span className='text-xl font-bold text-[#00E5FF]'>{formatCurrency(cartTotal)}</span>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className='border-t border-white/10 pt-4'>
                  <Label className='text-gray-400 text-sm mb-2 block'>Payment Method</Label>
                  <div className='grid grid-cols-3 gap-2'>
                    <Button
                      variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                      onClick={() => setPaymentMethod('cash')}
                      className={`flex flex-col items-center py-3 h-auto ${
                        paymentMethod === 'cash' 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'border-white/20 text-white hover:bg-white/5'
                      }`}
                      data-testid='payment-cash'
                    >
                      <Banknote className='w-5 h-5 mb-1' />
                      <span className='text-xs'>Cash</span>
                    </Button>
                    <Button
                      variant={paymentMethod === 'upi' ? 'default' : 'outline'}
                      onClick={() => setPaymentMethod('upi')}
                      disabled={isOffline}
                      className={`flex flex-col items-center py-3 h-auto ${
                        paymentMethod === 'upi' 
                          ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                          : 'border-white/20 text-white hover:bg-white/5'
                      } ${isOffline ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid='payment-upi'
                    >
                      <Smartphone className='w-5 h-5 mb-1' />
                      <span className='text-xs'>UPI</span>
                    </Button>
                    <Button
                      variant={paymentMethod === 'card' ? 'default' : 'outline'}
                      onClick={() => setPaymentMethod('card')}
                      disabled={isOffline}
                      className={`flex flex-col items-center py-3 h-auto ${
                        paymentMethod === 'card' 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'border-white/20 text-white hover:bg-white/5'
                      } ${isOffline ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid='payment-card'
                    >
                      <CreditCard className='w-5 h-5 mb-1' />
                      <span className='text-xs'>Card</span>
                    </Button>
                  </div>
                  {isOffline && paymentMethod !== 'cash' && (
                    <p className='text-yellow-400 text-xs mt-2'>⚠️ Only cash available offline</p>
                  )}
                </div>

                {/* Actions */}
                <div className='space-y-2'>
                  <Button
                    onClick={() => { handleCheckout(); setShowMobileCart(false); }}
                    disabled={loading || cart.length === 0}
                    className='w-full bg-[#00C853] hover:bg-[#00E676] text-white py-6 text-lg font-bold'
                  >
                    {loading ? 'Processing...' : 'Place Order'}
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() => setCart([])}
                    disabled={cart.length === 0}
                    className='w-full border-white/20 text-white hover:bg-white/5'
                  >
                    Clear Cart
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Print Invoice Dialog */}
          <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
            <DialogContent className='bg-[#121212] border-white/10 text-white max-w-sm'>
              <DialogHeader>
                <DialogTitle className='text-xl font-bold flex items-center gap-2' style={{ fontFamily: 'Bebas Neue' }}>
                  <CheckCircle className='w-6 h-6 text-green-500' />
                  Order Placed!
                </DialogTitle>
              </DialogHeader>
              
              {orderToPrint && (
                <div className='space-y-4'>
                  <div className='text-center py-4'>
                    <p className='text-3xl font-bold text-[#00E5FF]' style={{ fontFamily: 'JetBrains Mono' }}>
                      {orderToPrint.order_number}
                    </p>
                    <p className='text-gray-400 mt-1'>{orderToPrint.customer_name}</p>
                  </div>
                  
                  <div className='bg-white/5 rounded-lg p-3 text-sm'>
                    <div className='flex justify-between mb-1'>
                      <span className='text-gray-400'>Total Amount</span>
                      <span className='text-white font-bold'>{formatCurrency(orderToPrint.total_amount)}</span>
                    </div>
                    <div className='flex justify-between mb-1'>
                      <span className='text-gray-400'>Type</span>
                      <span className='text-white'>{orderToPrint.order_type === 'dine_in' ? 'Dine In' : 'Takeaway'}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span className='text-gray-400'>Payment</span>
                      <span className={`font-medium ${
                        orderToPrint.payment_method === 'cash' ? 'text-green-400' :
                        orderToPrint.payment_method === 'upi' ? 'text-purple-400' : 'text-blue-400'
                      }`}>
                        {orderToPrint.payment_method === 'cash' ? '💵 Cash' :
                         orderToPrint.payment_method === 'upi' ? '📱 UPI' : '💳 Card'}
                      </span>
                    </div>
                    {orderToPrint.offline && (
                      <div className='mt-2 pt-2 border-t border-white/10'>
                        <Badge className='bg-yellow-500/20 text-yellow-400 border-yellow-500/30'>
                          ⚡ Offline Order - Pending Sync
                        </Badge>
                      </div>
                    )}
                  </div>
                  
                  <div className='flex gap-2'>
                    <Button
                      onClick={() => handlePrintInvoice(orderToPrint)}
                      className='flex-1 bg-[#00E5FF] hover:bg-[#00B8D4] text-black'
                    >
                      <Printer className='w-4 h-4 mr-2' />
                      Print Invoice
                    </Button>
                    <Button
                      variant='outline'
                      onClick={() => setShowPrintDialog(false)}
                      className='flex-1 border-white/20 text-white hover:bg-white/5'
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
          </>
        )}
      </div>

      {/* Orders Dialog */}
      <Dialog open={showOrders} onOpenChange={setShowOrders}>
        <DialogContent className='bg-[#121212] border-white/10 text-white w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-3 sm:p-6'>
          <DialogHeader>
            <DialogTitle className='text-xl sm:text-2xl font-bold' style={{ fontFamily: 'Bebas Neue' }}>
              All Orders
            </DialogTitle>
          </DialogHeader>
          
          {/* Order Search Bar - Mobile optimized */}
          <div className='bg-[#1A1A1A] rounded-lg p-3 sm:p-4 mb-4 space-y-3'>
            <div className='flex flex-col sm:flex-row gap-2'>
              <div className='flex-1'>
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500' />
                  <Input
                    placeholder='Search by order #, name, o...'
                    value={orderSearchQuery}
                    onChange={(e) => setOrderSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchOrders()}
                    className='pl-10 bg-[#121212] border-white/10 text-white text-sm'
                    data-testid='order-search-input'
                  />
                </div>
              </div>
              <div className='flex gap-2'>
                <Input
                  type='date'
                  value={orderSearchDate}
                  onChange={(e) => setOrderSearchDate(e.target.value)}
                  className='bg-[#121212] border-white/10 text-white text-sm w-[130px] sm:w-[150px]'
                  data-testid='order-search-date'
                />
                <Select value={orderStatusFilter} onValueChange={setOrderStatusFilter}>
                  <SelectTrigger className='w-[100px] sm:w-[130px] bg-[#121212] border-white/10 text-white text-sm'>
                    <SelectValue placeholder='Status' />
                  </SelectTrigger>
                  <SelectContent className='bg-[#121212] border-white/10'>
                    <SelectItem value='all'>All Status</SelectItem>
                    <SelectItem value='placed'>Placed</SelectItem>
                    <SelectItem value='accepted'>Accepted</SelectItem>
                    <SelectItem value='preparing'>Preparing</SelectItem>
                    <SelectItem value='ready'>Ready</SelectItem>
                    <SelectItem value='delivered'>Delivered</SelectItem>
                    <SelectItem value='cancelled'>Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={searchOrders}
                  size='sm'
                  className='bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black px-3'
                  disabled={searchingOrders}
                  data-testid='order-search-btn'
                >
                  {searchingOrders ? <RefreshCw className='w-4 h-4 animate-spin' /> : <Search className='w-4 h-4' />}
                </Button>
              </div>
            </div>
            {(orderSearchQuery || orderSearchDate || orderStatusFilter !== 'all') && (
              <Button
                onClick={clearOrderSearch}
                variant='outline'
                size='sm'
                className='border-white/20 text-white hover:bg-white/5 w-full sm:w-auto'
              >
                Clear Filters
              </Button>
            )}
          </div>
          
          <Tabs defaultValue='new' className='w-full'>
            <TabsList className='bg-[#1A1A1A] border border-white/10 w-full overflow-x-auto flex-nowrap justify-start'>
              <TabsTrigger value='new' className='relative text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap'>
                New
                {newOrderCount > 0 && (
                  <span className='ml-1 bg-red-500 text-white text-xs rounded-full px-1.5'>
                    {newOrderCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value='accepted' className='text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap'>Accepted</TabsTrigger>
              <TabsTrigger value='preparing' className='text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap'>Preparing</TabsTrigger>
              <TabsTrigger value='ready' className='text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap'>Ready</TabsTrigger>
              <TabsTrigger value='out_for_delivery' className='text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap'>Dispatched</TabsTrigger>
              <TabsTrigger value='all' className='text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap'>All</TabsTrigger>
            </TabsList>
            
            {/* New Orders Tab - Online Orders Waiting for Acceptance (POS orders skip this) */}
            <TabsContent value='new' className='space-y-3'>
              {orders.filter(o => o.status === 'placed' && o.source !== 'pos').length === 0 ? (
                <div className='text-center py-8 text-gray-500'>
                  <CheckCircle className='w-12 h-12 mx-auto mb-2 opacity-50' />
                  <p>No new online orders waiting for acceptance</p>
                </div>
              ) : (
                orders.filter(o => o.status === 'placed' && o.source !== 'pos').map(order => (
                  <Card 
                    key={order.id} 
                    className='bg-[#1A1A1A] border-yellow-500/50 cursor-pointer hover:border-yellow-500 transition-all'
                    onClick={() => { setSelectedOrder(order); setShowOrderDetails(true); }}
                  >
                    <CardContent className='p-4'>
                      <div className='flex items-start justify-between mb-3'>
                        <div>
                          <h3 className='text-lg font-bold text-white' style={{ fontFamily: 'JetBrains Mono' }}>
                            {order.order_number}
                          </h3>
                          <p className='text-sm text-gray-400'>{order.customer_name}</p>
                          <p className='text-xs text-gray-500 mt-0.5'>
                            {new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} • {new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </p>
                          {order.order_type === 'delivery' && (
                            <Badge className='mt-1 bg-blue-500/20 text-blue-400 border-blue-500/30'>
                              <MapPin className='w-3 h-3 mr-1' /> Delivery
                            </Badge>
                          )}
                        </div>
                        <Badge className='bg-yellow-500/20 text-yellow-400 border-yellow-500/30 animate-pulse'>
                          ⏳ New Order
                        </Badge>
                      </div>
                      <div className='space-y-1 mb-3'>
                        {order.items.slice(0, 3).map((item, idx) => (
                          <div key={idx} className='text-sm text-gray-300 flex items-center gap-2'>
                            {item.quantity}x {item.product_name}
                            {item.is_addon_product && (
                              <span className='text-xs bg-yellow-500/30 text-yellow-400 px-1.5 py-0.5 rounded-full'>Side</span>
                            )}
                          </div>
                        ))}
                      </div>
                        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
                          <span className='text-lg font-bold text-[#00E5FF]'>
                            {formatCurrency(order.total_amount)}
                          </span>
                          <div className='flex flex-wrap gap-2' onClick={(e) => e.stopPropagation()}>
                            {/* Edit Order Button */}
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => openEditOrderDialog(order)}
                              className='border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 px-2 sm:px-3'
                              title='Edit Order'
                            >
                              <FileText className='w-4 h-4' />
                            </Button>
                            {/* Delete Order Button */}
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => openDeleteConfirm(order)}
                              className='border-red-500/50 text-red-400 hover:bg-red-500/10 px-2 sm:px-3'
                              title='Delete Order'
                            >
                              <Trash2 className='w-4 h-4' />
                            </Button>
                            {/* Print Invoice Button */}
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => handlePrintExistingOrder(order)}
                              className='border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10 px-2 sm:px-3'
                              title='Print Invoice'
                            >
                              <Printer className='w-4 h-4' />
                            </Button>
                            <Button
                              size='sm'
                              onClick={() => acceptOrder(order.id)}
                              className='bg-green-500 hover:bg-green-600 text-xs sm:text-sm px-2 sm:px-3'
                            >
                              <CheckCircle className='w-4 h-4 sm:mr-1' />
                              <span className='hidden sm:inline'>Accept</span>
                            </Button>
                            <Button
                              size='sm'
                              onClick={() => rejectOrder(order.id)}
                              className='bg-red-500 hover:bg-red-600 text-xs sm:text-sm px-2 sm:px-3'
                            >
                              <X className='w-4 h-4 sm:mr-1' />
                              <span className='hidden sm:inline'>Reject</span>
                            </Button>
                          </div>
                        </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
            
            {['accepted', 'preparing', 'ready', 'out_for_delivery', 'all'].map(tab => (
              <TabsContent key={tab} value={tab} className='space-y-3'>
                {orders
                  .filter(o => tab === 'all' || o.status === tab)
                  .map(order => (
                    <Card 
                      key={order.id} 
                      className='bg-[#1A1A1A] border-white/10 cursor-pointer hover:border-[#FF4500]/50 transition-all'
                      onClick={() => { setSelectedOrder(order); setShowOrderDetails(true); }}
                    >
                      <CardContent className='p-4'>
                        <div className='flex items-start justify-between mb-3'>
                          <div>
                            <h3 className='text-lg font-bold text-white' style={{ fontFamily: 'JetBrains Mono' }}>
                              {order.order_number}
                            </h3>
                            <p className='text-sm text-gray-400'>{order.customer_name}</p>
                            <p className='text-xs text-gray-500 mt-0.5'>
                              {new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} • {new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                              {order.source === 'pos' && <span className='ml-2 text-cyan-400'>(POS)</span>}
                            </p>
                            {order.order_type === 'delivery' && (
                              <Badge className='mt-1 bg-blue-500/20 text-blue-400 border-blue-500/30'>
                                <MapPin className='w-3 h-3 mr-1' /> Delivery
                              </Badge>
                            )}
                          </div>
                          <Badge className={getStatusColor(order.status)}>
                            {getStatusLabel(order.status)}
                          </Badge>
                        </div>
                        <div className='space-y-1 mb-3'>
                          {order.items.slice(0, 3).map((item, idx) => (
                            <div key={idx} className='text-sm text-gray-300 flex items-center gap-2'>
                              {item.quantity}x {item.product_name}
                              {item.is_addon_product && (
                                <span className='text-xs bg-yellow-500/30 text-yellow-400 px-1.5 py-0.5 rounded-full'>Side</span>
                              )}
                            </div>
                          ))}
                          {order.items.length > 3 && (
                            <div className='text-sm text-gray-500'>+{order.items.length - 3} more items</div>
                          )}
                        </div>
                        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
                          <span className='text-lg font-bold text-[#00E5FF]'>
                            {formatCurrency(order.total_amount)}
                          </span>
                          <div className='flex flex-wrap gap-2' onClick={(e) => e.stopPropagation()}>
                            {/* Edit Order Button - Available for all orders */}
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => openEditOrderDialog(order)}
                              className='border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 px-2'
                              title='Edit Order'
                            >
                              <FileText className='w-4 h-4' />
                            </Button>
                            {/* Delete Order Button - Available for all orders */}
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => openDeleteConfirm(order)}
                              className='border-red-500/50 text-red-400 hover:bg-red-500/10 px-2'
                              title='Delete Order'
                            >
                              <Trash2 className='w-4 h-4' />
                            </Button>
                            {/* Print Invoice Button - Always visible */}
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() => handlePrintExistingOrder(order)}
                              className='border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10 px-2'
                              title='Print Invoice'
                            >
                              <Printer className='w-4 h-4' />
                            </Button>
                            {order.status === 'accepted' && (
                              <Button
                                size='sm'
                                onClick={() => updateOrderStatus(order.id, 'preparing')}
                                className='bg-orange-500 hover:bg-orange-600 text-xs sm:text-sm whitespace-nowrap'
                              >
                                <span className='hidden sm:inline'>Start Preparing</span>
                                <span className='sm:hidden'>Prepare</span>
                              </Button>
                            )}
                            {order.status === 'preparing' && (
                              <Button
                                size='sm'
                                onClick={() => updateOrderStatus(order.id, 'ready')}
                                className='bg-green-500 hover:bg-green-600 text-xs sm:text-sm whitespace-nowrap'
                              >
                                <span className='hidden sm:inline'>Mark Ready</span>
                                <span className='sm:hidden'>Ready</span>
                              </Button>
                            )}
                            {order.status === 'ready' && order.order_type === 'delivery' && (
                              <Button
                                size='sm'
                                onClick={() => updateOrderStatus(order.id, 'out_for_delivery')}
                                className='bg-purple-500 hover:bg-purple-600 text-xs sm:text-sm'
                              >
                                Dispatch
                              </Button>
                            )}
                            {order.status === 'ready' && order.order_type !== 'delivery' && (
                              <Button
                                size='sm'
                                onClick={() => updateOrderStatus(order.id, 'completed')}
                                className='bg-emerald-500 hover:bg-emerald-600 text-xs sm:text-sm'
                              >
                                Complete
                              </Button>
                            )}
                            {order.status === 'out_for_delivery' && (
                              <Button
                                size='sm'
                                onClick={() => updateOrderStatus(order.id, 'delivered')}
                                className='bg-emerald-500 hover:bg-emerald-600 text-xs sm:text-sm whitespace-nowrap'
                              >
                                <span className='hidden sm:inline'>Mark Delivered</span>
                                <span className='sm:hidden'>Delivered</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog with Copy Buttons */}
      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-2xl font-bold flex items-center gap-2' style={{ fontFamily: 'Bebas Neue' }}>
              <Package className='w-6 h-6 text-[#FF4500]' />
              Order Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className='space-y-4'>
              {/* Order Number with Copy */}
              <div className='bg-white/5 rounded-lg p-3 flex items-center justify-between'>
                <div>
                  <p className='text-xs text-gray-500 uppercase'>Order ID</p>
                  <p className='text-lg font-bold text-white' style={{ fontFamily: 'JetBrains Mono' }}>
                    {selectedOrder.order_number}
                  </p>
                </div>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => copyToClipboard(selectedOrder.order_number, 'Order ID')}
                  className='border-white/20 hover:bg-white/10'
                >
                  <Copy className='w-4 h-4' />
                </Button>
              </div>

              {/* Status Badge */}
              <div className='flex items-center gap-2'>
                <Badge className={getStatusColor(selectedOrder.status)}>
                  {getStatusLabel(selectedOrder.status)}
                </Badge>
                <Badge className={selectedOrder.order_type === 'delivery' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}>
                  {selectedOrder.order_type === 'delivery' ? 'Delivery' : selectedOrder.order_type === 'pickup' ? 'Pickup' : 'Dine-in'}
                </Badge>
              </div>

              {/* Customer Name with Copy */}
              <div className='bg-white/5 rounded-lg p-3 flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <User className='w-5 h-5 text-gray-400' />
                  <div>
                    <p className='text-xs text-gray-500 uppercase'>Customer Name</p>
                    <p className='text-white font-medium'>{selectedOrder.customer_name}</p>
                  </div>
                </div>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => copyToClipboard(selectedOrder.customer_name, 'Name')}
                  className='border-white/20 hover:bg-white/10'
                >
                  <Copy className='w-4 h-4' />
                </Button>
              </div>

              {/* Phone Number with Copy */}
              <div className='bg-white/5 rounded-lg p-3 flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <Phone className='w-5 h-5 text-green-400' />
                  <div>
                    <p className='text-xs text-gray-500 uppercase'>Phone Number</p>
                    <p className='text-white font-medium'>{selectedOrder.customer_phone}</p>
                  </div>
                </div>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => copyToClipboard(selectedOrder.customer_phone, 'Phone')}
                  className='border-white/20 hover:bg-white/10'
                >
                  <Copy className='w-4 h-4' />
                </Button>
              </div>

              {/* Delivery Address with Copy (only for delivery orders) */}
              {(selectedOrder.order_type === 'delivery' || selectedOrder.delivery_address || selectedOrder.customer_address) && (
                <div className='bg-white/5 rounded-lg p-3'>
                  <div className='flex items-start justify-between'>
                    <div className='flex items-start gap-3 flex-1'>
                      <MapPin className='w-5 h-5 text-blue-400 mt-0.5' />
                      <div className='flex-1'>
                        <p className='text-xs text-gray-500 uppercase'>Delivery Address</p>
                        <p className='text-white text-sm'>
                          {selectedOrder.delivery_address || selectedOrder.customer_address || 'No address provided'}
                        </p>
                      </div>
                    </div>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => copyToClipboard(selectedOrder.delivery_address || selectedOrder.customer_address || '', 'Address')}
                      className='border-white/20 hover:bg-white/10 ml-2'
                    >
                      <Copy className='w-4 h-4' />
                    </Button>
                  </div>
                </div>
              )}

              {/* GPS Coordinates - Easy Copy for Delivery Apps */}
              {selectedOrder.order_type === 'delivery' && selectedOrder.delivery_latitude && selectedOrder.delivery_longitude && (
                <div className='bg-green-500/10 border border-green-500/30 rounded-lg p-3'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <MapPin className='w-4 h-4 text-green-400' />
                      <div>
                        <p className='text-xs text-green-400 uppercase font-medium'>GPS Coordinates</p>
                        <p className='text-white text-sm font-mono'>
                          {parseFloat(selectedOrder.delivery_latitude).toFixed(6)}, {parseFloat(selectedOrder.delivery_longitude).toFixed(6)}
                        </p>
                      </div>
                    </div>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => copyToClipboard(`${selectedOrder.delivery_latitude}, ${selectedOrder.delivery_longitude}`, 'GPS Coordinates')}
                      className='border-green-500/30 hover:bg-green-500/20 text-green-400'
                    >
                      <Copy className='w-4 h-4 mr-1' />
                      Copy
                    </Button>
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div className='bg-white/5 rounded-lg p-3'>
                <p className='text-xs text-gray-500 uppercase mb-2'>Order Items</p>
                <div className='space-y-2'>
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className={`flex justify-between text-sm p-2 rounded ${item.is_addon_product ? 'bg-yellow-500/10 border border-yellow-500/30' : ''}`}>
                      <div className='flex items-center gap-2'>
                        <span className='text-gray-300'>{item.quantity}x {item.product_name}</span>
                        {item.is_addon_product && (
                          <span className='text-xs bg-yellow-500/30 text-yellow-400 px-2 py-0.5 rounded-full'>Side</span>
                        )}
                      </div>
                      <span className='text-white'>{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  ))}
                  <div className='border-t border-white/10 pt-2 mt-2 flex justify-between font-bold'>
                    <span className='text-white'>Total</span>
                    <span className='text-[#00E5FF]'>{formatCurrency(selectedOrder.total_amount)}</span>
                  </div>
                </div>
              </div>

              {/* View on Google Maps Button */}
              {selectedOrder.order_type === 'delivery' && (() => {
                const lat = selectedOrder.delivery_latitude;
                const lng = selectedOrder.delivery_longitude;
                const address = selectedOrder.customer_address || selectedOrder.delivery_address;
                const hasValidCoords = lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));
                
                if (hasValidCoords) {
                  return (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${parseFloat(lat)},${parseFloat(lng)}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center justify-center gap-2 w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-bold transition-colors'
                    >
                      <MapPin className='w-5 h-5' />
                      Open in Google Maps
                    </a>
                  );
                } else if (address) {
                  return (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center justify-center gap-2 w-full bg-blue-500/70 hover:bg-blue-600 text-white py-3 rounded-lg font-bold transition-colors'
                    >
                      <MapPin className='w-5 h-5' />
                      Search Address on Maps
                    </a>
                  );
                }
                return null;
              })()}

              {/* Copy All for Delivery Button */}
              {selectedOrder.order_type === 'delivery' && (
                <Button
                  onClick={() => copyAllDeliveryDetails(selectedOrder)}
                  className='w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6 text-lg font-bold'
                >
                  <Copy className='w-5 h-5 mr-2' />
                  Copy All Details for Delivery
                </Button>
              )}

              {/* Print Invoice Button */}
              <Button
                onClick={() => handlePrintExistingOrder(selectedOrder)}
                variant='outline'
                className='w-full border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10'
              >
                <FileText className='w-4 h-4 mr-2' />
                Print Invoice
              </Button>

              {/* Status Update Buttons */}
              <div className='flex gap-2'>
                {selectedOrder.status === 'placed' && (
                  <>
                    <Button
                      onClick={() => acceptOrder(selectedOrder.id)}
                      className='flex-1 bg-green-500 hover:bg-green-600'
                    >
                      <CheckCircle className='w-4 h-4 mr-2' />
                      Accept Order
                    </Button>
                    <Button
                      onClick={() => initPartialReject(selectedOrder)}
                      variant='outline'
                      className='flex-1 border-orange-500/50 text-orange-400 hover:bg-orange-500/10'
                    >
                      Partial Reject
                    </Button>
                    <Button
                      onClick={() => rejectOrder(selectedOrder.id)}
                      className='flex-1 bg-red-500 hover:bg-red-600'
                    >
                      <X className='w-4 h-4 mr-2' />
                      Full Reject
                    </Button>
                  </>
                )}
                {selectedOrder.status === 'accepted' && (
                  <>
                    <Button
                      onClick={() => { updateOrderStatus(selectedOrder.id, 'preparing'); setShowOrderDetails(false); }}
                      className='flex-1 bg-orange-500 hover:bg-orange-600'
                    >
                      Start Preparing
                    </Button>
                    <Button
                      onClick={() => initPartialReject(selectedOrder)}
                      variant='outline'
                      className='border-orange-500/50 text-orange-400 hover:bg-orange-500/10'
                    >
                      Partial Reject
                    </Button>
                  </>
                )}
                {selectedOrder.status === 'preparing' && (
                  <Button
                    onClick={() => { updateOrderStatus(selectedOrder.id, 'ready'); setShowOrderDetails(false); }}
                    className='flex-1 bg-green-500 hover:bg-green-600'
                  >
                    Mark Ready
                  </Button>
                )}
                {selectedOrder.status === 'ready' && selectedOrder.order_type === 'delivery' && (
                  <Button
                    onClick={() => { updateOrderStatus(selectedOrder.id, 'out_for_delivery'); setShowOrderDetails(false); }}
                    className='flex-1 bg-purple-500 hover:bg-purple-600'
                  >
                    <Truck className='w-4 h-4 mr-2' />
                    Dispatch Order
                  </Button>
                )}
                {selectedOrder.status === 'ready' && selectedOrder.order_type !== 'delivery' && (
                  <Button
                    onClick={() => { updateOrderStatus(selectedOrder.id, 'completed'); setShowOrderDetails(false); }}
                    className='flex-1 bg-emerald-500 hover:bg-emerald-600'
                  >
                    <CheckCircle className='w-4 h-4 mr-2' />
                    Complete Order
                  </Button>
                )}
                {selectedOrder.status === 'out_for_delivery' && (
                  <Button
                    onClick={() => { updateOrderStatus(selectedOrder.id, 'delivered'); setShowOrderDetails(false); }}
                    className='flex-1 bg-emerald-500 hover:bg-emerald-600'
                  >
                    <CheckCircle className='w-4 h-4 mr-2' />
                    Mark Delivered
                  </Button>
                )}
                {/* Refund button for rejected/cancelled orders with online payment */}
                {['rejected', 'cancelled'].includes(selectedOrder.status) && 
                 selectedOrder.razorpay_payment_id && 
                 !selectedOrder.refund_id && (
                  <Button
                    onClick={() => {
                      setRefundOrder(selectedOrder);
                      setShowRefundDialog(true);
                    }}
                    className='flex-1 bg-orange-500 hover:bg-orange-600'
                  >
                    Process Refund
                  </Button>
                )}
                {selectedOrder.refund_id && (
                  <div className='w-full p-2 bg-green-500/20 rounded-lg text-center'>
                    <p className='text-green-400 text-sm'>Refund Processed: {selectedOrder.refund_id}</p>
                    <p className='text-gray-400 text-xs'>Amount: {formatCurrency(selectedOrder.refund_amount)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stock Alerts Dialog */}
      <Dialog open={showStockAlerts} onOpenChange={setShowStockAlerts}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-white flex items-center gap-2'>
              <AlertTriangle className='w-5 h-5 text-yellow-500' />
              Critical Stock Alerts
            </DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            {criticalStockAlerts.length === 0 ? (
              <p className='text-gray-400 text-center py-4'>No critical stock alerts</p>
            ) : (
              criticalStockAlerts.map((alert, idx) => (
                <div key={idx} className={`p-3 rounded-lg border ${alert.stock === 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                  <div className='flex justify-between items-start'>
                    <div>
                      <p className='font-medium text-white'>{alert.name || alert.product_name}</p>
                      <p className='text-sm text-gray-400'>Critical limit: {alert.critical_limit}</p>
                    </div>
                    <Badge className={alert.stock === 0 ? 'bg-red-500' : 'bg-yellow-500 text-black'}>
                      {alert.stock === 0 ? 'OUT OF STOCK' : `${alert.stock} left`}
                    </Badge>
                  </div>
                </div>
              ))
            )}
            <Button 
              onClick={fetchCriticalStockAlerts}
              variant='outline'
              className='w-full border-white/20 text-white'
            >
              <RefreshCw className='w-4 h-4 mr-2' />
              Refresh Alerts
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund Confirmation Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-sm'>
          <DialogHeader>
            <DialogTitle className='text-white'>Process Refund</DialogTitle>
          </DialogHeader>
          {refundOrder && (
            <div className='space-y-4'>
              <p className='text-gray-300'>
                Process refund for order <span className='text-[#00E5FF] font-mono'>{refundOrder.order_number}</span>?
              </p>
              <div className='bg-white/5 p-3 rounded-lg'>
                <p className='text-sm text-gray-400'>Refund Amount</p>
                <p className='text-2xl text-white font-bold'>{formatCurrency(refundOrder.total_amount)}</p>
              </div>
              <p className='text-xs text-gray-400'>
                Refund will be processed to the original payment method. It may take 5-7 business days to reflect.
              </p>
              <div className='flex gap-2'>
                <Button
                  onClick={() => setShowRefundDialog(false)}
                  variant='outline'
                  className='flex-1 border-white/20 text-white'
                  disabled={refundLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => processRefund(refundOrder)}
                  className='flex-1 bg-red-500 hover:bg-red-600 text-white'
                  disabled={refundLoading}
                >
                  {refundLoading ? 'Processing...' : 'Confirm Refund'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Partial Rejection Dialog */}
      <Dialog open={showPartialRejectDialog} onOpenChange={setShowPartialRejectDialog}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-white'>Partial Order Rejection</DialogTitle>
          </DialogHeader>
          {partialRejectOrder && (
            <div className='space-y-4'>
              <p className='text-gray-400 text-sm'>
                Order: <span className='text-[#00E5FF] font-mono'>{partialRejectOrder.order_number}</span>
              </p>
              
              <div className='space-y-3'>
                <p className='text-sm text-gray-300 font-medium'>Select items to reject:</p>
                {partialRejectItems.map((item, idx) => (
                  <div key={idx} className={`p-3 rounded-lg ${item.is_addon_product ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-white/5'}`}>
                    <div className='flex justify-between items-start mb-2'>
                      <div>
                        <div className='flex items-center gap-2'>
                          <p className='text-white font-medium'>{item.product_name}</p>
                          {item.is_addon_product && (
                            <span className='text-xs bg-yellow-500/30 text-yellow-400 px-2 py-0.5 rounded-full'>Side</span>
                          )}
                        </div>
                        {/* Show Add-ons in POS */}
                        {item.selected_addons && item.selected_addons.length > 0 && (
                          <div className='flex flex-wrap gap-1 mt-1'>
                            {item.selected_addons.map((addon, aIdx) => (
                              <span key={aIdx} className='text-xs bg-[#FF4500]/20 text-[#FF4500] px-1.5 py-0.5 rounded'>
                                +{addon.name}
                              </span>
                            ))}
                          </div>
                        )}
                        {item.selected_customizations && item.selected_customizations.length > 0 && (
                          <div className='flex flex-wrap gap-1 mt-1'>
                            {item.selected_customizations.map((cust, cIdx) => (
                              <span key={cIdx} className='text-xs bg-[#00E5FF]/20 text-[#00E5FF] px-1.5 py-0.5 rounded'>
                                {cust.option}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className='text-gray-400 text-sm'>{formatCurrency(item.price)} × {item.quantity}</p>
                      </div>
                      <p className='text-gray-300'>{formatCurrency(item.price * item.quantity)}</p>
                    </div>
                    <div className='flex items-center gap-3'>
                      <span className='text-sm text-gray-400'>Reject:</span>
                      <div className='flex items-center gap-2'>
                        <Button
                          size='sm'
                          variant='outline'
                          className='h-8 w-8 p-0 border-white/20'
                          onClick={() => updatePartialRejectQuantity(item.product_id, item.reject_quantity - 1)}
                          disabled={item.reject_quantity <= 0}
                        >
                          <Minus className='w-3 h-3' />
                        </Button>
                        <span className='w-8 text-center text-white font-mono'>{item.reject_quantity}</span>
                        <Button
                          size='sm'
                          variant='outline'
                          className='h-8 w-8 p-0 border-white/20'
                          onClick={() => updatePartialRejectQuantity(item.product_id, item.reject_quantity + 1)}
                          disabled={item.reject_quantity >= item.quantity}
                        >
                          <Plus className='w-3 h-3' />
                        </Button>
                      </div>
                      <span className='text-sm text-gray-400'>of {item.quantity}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <Label className='text-gray-400 text-sm'>Reason for rejection</Label>
                <Input
                  value={partialRejectReason}
                  onChange={(e) => setPartialRejectReason(e.target.value)}
                  placeholder='e.g., Items out of stock'
                  className='mt-1 bg-white/5 border-white/10 text-white'
                />
              </div>

              <div className='bg-orange-500/10 border border-orange-500/30 p-3 rounded-lg'>
                <div className='flex justify-between items-center'>
                  <span className='text-orange-400'>Refund Amount</span>
                  <span className='text-orange-400 font-bold text-lg'>
                    {formatCurrency(calculatePartialRefundAmount())}
                  </span>
                </div>
                {!partialRejectOrder.razorpay_payment_id && (
                  <p className='text-xs text-gray-400 mt-1'>COD order - refund manually if needed</p>
                )}
              </div>

              <div className='flex gap-2'>
                <Button
                  onClick={() => {
                    setShowPartialRejectDialog(false);
                    setPartialRejectOrder(null);
                    setPartialRejectItems([]);
                  }}
                  variant='outline'
                  className='flex-1 border-white/20 text-white'
                  disabled={refundLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={processPartialRejection}
                  className='flex-1 bg-orange-500 hover:bg-orange-600 text-white'
                  disabled={refundLoading || calculatePartialRefundAmount() === 0}
                >
                  {refundLoading ? 'Processing...' : 'Confirm Partial Reject'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Wastage Tracking Dialog */}
      <Dialog open={showWastageDialog} onOpenChange={setShowWastageDialog}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold flex items-center gap-2' style={{ fontFamily: 'Bebas Neue' }}>
              <Trash2 className='w-5 h-5 text-red-400' />
              Log Wastage
            </DialogTitle>
          </DialogHeader>
          
          {wastageProduct && (
            <div className='space-y-4'>
              <div className='bg-[#1A1A1A] rounded-lg p-4'>
                <h4 className='text-white font-medium mb-1'>{wastageProduct.name}</h4>
                <p className='text-gray-400 text-sm'>Current Stock: {wastageProduct.stock || 0}</p>
              </div>
              
              <div>
                <Label className='text-gray-300'>Quantity to log as waste</Label>
                <div className='flex items-center gap-2 mt-1'>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => setWastageQuantity(Math.max(1, wastageQuantity - 1))}
                    className='border-white/20 text-white'
                  >
                    <Minus className='w-4 h-4' />
                  </Button>
                  <Input
                    type='number'
                    min={1}
                    max={wastageProduct.stock || 99}
                    value={wastageQuantity}
                    onChange={(e) => setWastageQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className='w-20 text-center bg-[#1A1A1A] border-white/10 text-white'
                    data-testid='wastage-quantity'
                  />
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => setWastageQuantity(Math.min(wastageProduct.stock || 99, wastageQuantity + 1))}
                    className='border-white/20 text-white'
                  >
                    <Plus className='w-4 h-4' />
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className='text-gray-300'>Reason for wastage *</Label>
                <Select value={wastageReason} onValueChange={setWastageReason}>
                  <SelectTrigger className='mt-1 bg-[#1A1A1A] border-white/10 text-white' data-testid='wastage-reason'>
                    <SelectValue placeholder='Select reason...' />
                  </SelectTrigger>
                  <SelectContent className='bg-[#121212] border-white/10'>
                    <SelectItem value='expired'>Expired</SelectItem>
                    <SelectItem value='damaged'>Damaged</SelectItem>
                    <SelectItem value='quality_issue'>Quality Issue</SelectItem>
                    <SelectItem value='spillage'>Spillage</SelectItem>
                    <SelectItem value='overcooked'>Overcooked</SelectItem>
                    <SelectItem value='customer_return'>Customer Return</SelectItem>
                    <SelectItem value='other'>Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className='text-gray-300'>Notes (optional)</Label>
                <Input
                  value={wastageNotes}
                  onChange={(e) => setWastageNotes(e.target.value)}
                  placeholder='Additional details...'
                  className='mt-1 bg-[#1A1A1A] border-white/10 text-white'
                />
              </div>
              
              <div className='bg-red-500/10 border border-red-500/30 rounded-lg p-3'>
                <p className='text-red-400 text-sm'>
                  ⚠️ This will deduct {wastageQuantity} unit(s) from inventory automatically.
                </p>
              </div>
              
              <div className='flex gap-2'>
                <Button
                  onClick={() => setShowWastageDialog(false)}
                  variant='outline'
                  className='flex-1 border-white/20 text-white'
                  disabled={wastageLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitWastage}
                  className='flex-1 bg-red-500 hover:bg-red-600 text-white'
                  disabled={wastageLoading || !wastageReason}
                  data-testid='submit-wastage-btn'
                >
                  {wastageLoading ? 'Logging...' : 'Log Wastage'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Wastage History Dialog */}
      <Dialog open={showWastageHistory} onOpenChange={(open) => { setShowWastageHistory(open); if(open) fetchWastageHistory(); }}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold' style={{ fontFamily: 'Bebas Neue' }}>
              Wastage History
            </DialogTitle>
          </DialogHeader>
          
          <div className='space-y-3'>
            {wastageHistory.length === 0 ? (
              <div className='text-center py-8 text-gray-500'>
                <Trash2 className='w-12 h-12 mx-auto mb-2 opacity-50' />
                <p>No wastage records found</p>
              </div>
            ) : (
              wastageHistory.map((record, idx) => (
                <Card key={idx} className='bg-[#1A1A1A] border-white/10'>
                  <CardContent className='p-4'>
                    <div className='flex justify-between items-start'>
                      <div>
                        <h4 className='text-white font-medium'>{record.product_name}</h4>
                        <p className='text-gray-400 text-sm'>Qty: {record.quantity} • Reason: {record.reason}</p>
                        <p className='text-gray-500 text-xs mt-1'>
                          {new Date(record.created_at).toLocaleString('en-IN')} • By: {record.reported_by}
                        </p>
                      </div>
                      <Badge className='bg-red-500/20 text-red-400 border-red-500/30'>
                        -{record.quantity}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={showEditOrderDialog} onOpenChange={setShowEditOrderDialog}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold' style={{ fontFamily: 'Bebas Neue' }}>
              Edit Order {editingOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          
          {editingOrder && (
            <div className='space-y-4'>
              {/* Customer Name */}
              <div>
                <Label className='text-gray-400 text-sm'>Customer Name</Label>
                <Input
                  value={editCustomerName}
                  onChange={(e) => setEditCustomerName(e.target.value)}
                  className='bg-[#1A1A1A] border-white/10 text-white mt-1'
                  placeholder='Customer name'
                />
              </div>
              
              {/* Order Type */}
              <div>
                <Label className='text-gray-400 text-sm'>Order Type</Label>
                <Select value={editOrderType} onValueChange={setEditOrderType}>
                  <SelectTrigger className='bg-[#1A1A1A] border-white/10 text-white mt-1'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='bg-[#121212] border-white/10'>
                    <SelectItem value='dine_in'>Dine In</SelectItem>
                    <SelectItem value='pickup'>Pickup</SelectItem>
                    <SelectItem value='delivery'>Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Payment Method */}
              <div>
                <Label className='text-gray-400 text-sm'>Payment Method</Label>
                <Select value={editPaymentMethod} onValueChange={setEditPaymentMethod}>
                  <SelectTrigger className='bg-[#1A1A1A] border-white/10 text-white mt-1'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='bg-[#121212] border-white/10'>
                    <SelectItem value='cash'>Cash</SelectItem>
                    <SelectItem value='upi'>UPI</SelectItem>
                    <SelectItem value='card'>Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Order Items */}
              <div>
                <Label className='text-gray-400 text-sm mb-2 block'>Order Items</Label>
                <div className='space-y-2 max-h-[200px] overflow-y-auto'>
                  {editOrderItems.map((item, idx) => (
                    <div key={idx} className='flex items-center justify-between bg-[#1A1A1A] p-3 rounded-lg'>
                      <div className='flex-1'>
                        <p className='text-white text-sm'>{item.product_name}</p>
                        <p className='text-gray-400 text-xs'>₹{item.price} each</p>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => updateEditItemQuantity(idx, -1)}
                          className='h-8 w-8 p-0 border-white/20'
                        >
                          <Minus className='w-4 h-4' />
                        </Button>
                        <span className='text-white w-8 text-center'>{item.quantity}</span>
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => updateEditItemQuantity(idx, 1)}
                          className='h-8 w-8 p-0 border-white/20'
                        >
                          <Plus className='w-4 h-4' />
                        </Button>
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => removeEditItem(idx)}
                          className='h-8 w-8 p-0 border-red-500/50 text-red-400 ml-2'
                        >
                          <Trash2 className='w-4 h-4' />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {editOrderItems.length === 0 && (
                    <p className='text-gray-500 text-center py-4'>No items in order</p>
                  )}
                </div>
              </div>
              
              {/* Totals */}
              <div className='bg-[#1A1A1A] p-3 rounded-lg space-y-1'>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-400'>Subtotal</span>
                  <span className='text-white'>₹{calculateEditTotal().subtotal}</span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-400'>GST ({editingOrder?.gst_rate || 5}%)</span>
                  <span className='text-white'>₹{calculateEditTotal().gst}</span>
                </div>
                <div className='flex justify-between text-lg font-bold border-t border-white/10 pt-2 mt-2'>
                  <span className='text-white'>Total</span>
                  <span className='text-[#00E5FF]'>₹{calculateEditTotal().total}</span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className='flex gap-3'>
                <Button
                  variant='outline'
                  onClick={() => setShowEditOrderDialog(false)}
                  className='flex-1 border-white/20 text-white'
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveEditedOrder}
                  disabled={editingLoading || editOrderItems.length === 0}
                  className='flex-1 bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black'
                >
                  {editingLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Order Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold text-red-400' style={{ fontFamily: 'Bebas Neue' }}>
              Delete Order?
            </DialogTitle>
          </DialogHeader>
          
          {deletingOrder && (
            <div className='space-y-4'>
              <div className='bg-red-500/10 border border-red-500/30 rounded-lg p-4'>
                <p className='text-white'>
                  Are you sure you want to delete order <span className='font-bold text-[#00E5FF]'>{deletingOrder.order_number}</span>?
                </p>
                <p className='text-gray-400 text-sm mt-2'>
                  This action cannot be undone. Stock will be restored for all items.
                </p>
              </div>
              
              <div className='bg-[#1A1A1A] p-3 rounded-lg'>
                <p className='text-gray-400 text-sm'>Order Details:</p>
                <p className='text-white'>{deletingOrder.customer_name || 'Walk-in Customer'}</p>
                <p className='text-[#00E5FF] font-bold'>{formatCurrency(deletingOrder.total_amount)}</p>
                <p className='text-gray-500 text-xs mt-1'>{deletingOrder.items?.length || 0} items</p>
              </div>
              
              <div className='flex gap-3'>
                <Button
                  variant='outline'
                  onClick={() => setShowDeleteConfirm(false)}
                  className='flex-1 border-white/20 text-white'
                >
                  Cancel
                </Button>
                <Button
                  onClick={deleteOrder}
                  disabled={deletingLoading}
                  className='flex-1 bg-red-500 hover:bg-red-600 text-white'
                >
                  {deletingLoading ? 'Deleting...' : 'Delete Order'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Today's Sales Summary Dialog */}
      <Dialog open={showTodaySummary} onOpenChange={setShowTodaySummary}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold text-green-400' style={{ fontFamily: 'Bebas Neue' }}>
              📊 Today&apos;s Sales Summary
            </DialogTitle>
          </DialogHeader>
          
          {loadingTodaySummary ? (
            <div className='flex items-center justify-center py-12'>
              <RefreshCw className='w-8 h-8 animate-spin text-[#00E5FF]' />
            </div>
          ) : todaySummary ? (
            <div className='space-y-4'>
              {/* Summary Cards */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                <Card className='bg-green-500/10 border-green-500/30'>
                  <CardContent className='p-3 text-center'>
                    <p className='text-green-400 text-xs'>Total Sales</p>
                    <p className='text-xl font-bold text-white'>₹{todaySummary.summary.total_sales.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className='bg-blue-500/10 border-blue-500/30'>
                  <CardContent className='p-3 text-center'>
                    <p className='text-blue-400 text-xs'>Orders</p>
                    <p className='text-xl font-bold text-white'>{todaySummary.summary.billable_orders}</p>
                  </CardContent>
                </Card>
                <Card className='bg-yellow-500/10 border-yellow-500/30'>
                  <CardContent className='p-3 text-center'>
                    <p className='text-yellow-400 text-xs'>Pending</p>
                    <p className='text-xl font-bold text-white'>{todaySummary.summary.pending_orders}</p>
                  </CardContent>
                </Card>
                <Card className='bg-red-500/10 border-red-500/30'>
                  <CardContent className='p-3 text-center'>
                    <p className='text-red-400 text-xs'>Cancelled</p>
                    <p className='text-xl font-bold text-white'>{todaySummary.summary.cancelled_orders}</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Payment Breakdown */}
              <Card className='bg-[#1A1A1A] border-white/10'>
                <CardContent className='p-4'>
                  <h4 className='text-white font-semibold mb-3'>💵 Cash Collection</h4>
                  <div className='grid grid-cols-3 gap-4 text-center'>
                    <div className='bg-green-500/10 rounded-lg p-3'>
                      <p className='text-green-400 text-sm'>Cash</p>
                      <p className='text-lg font-bold text-white'>₹{todaySummary.payment_breakdown.cash.toLocaleString()}</p>
                    </div>
                    <div className='bg-blue-500/10 rounded-lg p-3'>
                      <p className='text-blue-400 text-sm'>UPI</p>
                      <p className='text-lg font-bold text-white'>₹{todaySummary.payment_breakdown.upi.toLocaleString()}</p>
                    </div>
                    <div className='bg-purple-500/10 rounded-lg p-3'>
                      <p className='text-purple-400 text-sm'>Card</p>
                      <p className='text-lg font-bold text-white'>₹{todaySummary.payment_breakdown.card.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Items Sold */}
              <Card className='bg-[#1A1A1A] border-white/10'>
                <CardContent className='p-4'>
                  <h4 className='text-white font-semibold mb-3'>📦 Items Sold Today</h4>
                  {todaySummary.items_sold.length === 0 ? (
                    <p className='text-gray-500 text-center py-4'>No items sold yet</p>
                  ) : (
                    <div className='space-y-2 max-h-[250px] overflow-y-auto'>
                      {todaySummary.items_sold.map((item, idx) => (
                        <div key={idx} className='flex items-center justify-between bg-white/5 rounded-lg p-3'>
                          <div>
                            <p className='text-white font-medium'>{item.product_name}</p>
                            <p className='text-gray-400 text-xs'>₹{item.price} per unit</p>
                          </div>
                          <div className='text-right'>
                            <p className='text-[#00E5FF] font-bold'>{item.quantity} sold</p>
                            <p className='text-green-400 text-sm'>₹{item.total_value.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Wastage Summary */}
              {todaySummary.wastage.entries > 0 && (
                <Card className='bg-red-500/10 border-red-500/30'>
                  <CardContent className='p-3'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-2'>
                        <Trash2 className='w-5 h-5 text-red-400' />
                        <span className='text-red-400'>Wastage Today</span>
                      </div>
                      <span className='text-white font-bold'>{todaySummary.wastage.total_qty} units ({todaySummary.wastage.entries} entries)</span>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <Button
                onClick={() => fetchTodaySummary()}
                variant='outline'
                className='w-full border-white/20 text-white'
              >
                <RefreshCw className='w-4 h-4 mr-2' />
                Refresh
              </Button>
            </div>
          ) : (
            <p className='text-gray-500 text-center py-8'>No data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Stock Reconciliation Dialog */}
      <Dialog open={showReconciliation} onOpenChange={setShowReconciliation}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold' style={{ fontFamily: 'Bebas Neue' }}>
              <span className={isSunday ? 'text-yellow-400' : 'text-blue-400'}>
                📋 Stock Reconciliation
              </span>
              {isSunday && (
                <Badge className='ml-2 bg-yellow-500 text-black'>RECONCILIATION DAY</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {reconciliationLoading ? (
            <div className='flex items-center justify-center py-12'>
              <RefreshCw className='w-8 h-8 animate-spin text-[#00E5FF]' />
            </div>
          ) : (
            <div className='space-y-4'>
              <div className='bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm'>
                <p className='text-blue-400'>
                  ℹ️ Enter the actual physical count for each item. Only items with differences will be saved.
                </p>
              </div>
              
              {/* Reconciliation Table */}
              <div className='overflow-x-auto'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='bg-[#1A1A1A] text-gray-400'>
                      <th className='text-left p-3'>Product</th>
                      <th className='text-right p-3'>System Stock</th>
                      <th className='text-center p-3'>Physical Count</th>
                      <th className='text-right p-3'>Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliationData.map((item, idx) => {
                      const diff = (item.physical_count || 0) - item.system_stock;
                      return (
                        <tr key={idx} className='border-b border-white/5 hover:bg-white/5'>
                          <td className='p-3 text-white'>{item.product_name}</td>
                          <td className='p-3 text-right text-gray-400 font-mono'>{item.system_stock}</td>
                          <td className='p-3'>
                            <Input
                              type='number'
                              value={item.physical_count}
                              onChange={(e) => updatePhysicalCount(item.product_id, e.target.value)}
                              className='w-20 mx-auto bg-[#1A1A1A] border-white/20 text-white text-center'
                              min={0}
                            />
                          </td>
                          <td className={`p-3 text-right font-mono font-bold ${
                            diff === 0 ? 'text-gray-500' : diff > 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {diff > 0 ? '+' : ''}{diff}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Summary of Changes */}
              {reconciliationData.filter(p => p.physical_count !== p.system_stock).length > 0 && (
                <div className='bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3'>
                  <p className='text-yellow-400 font-medium'>
                    ⚠️ {reconciliationData.filter(p => p.physical_count !== p.system_stock).length} item(s) have differences
                  </p>
                </div>
              )}
              
              <div className='flex gap-3'>
                <Button
                  variant='outline'
                  onClick={() => setShowReconciliation(false)}
                  className='flex-1 border-white/20 text-white'
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveReconciliation}
                  disabled={savingReconciliation}
                  className='flex-1 bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black'
                >
                  {savingReconciliation ? 'Saving...' : 'Save Reconciliation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Wastage Entry Dialog */}
      <Dialog open={showQuickWastage} onOpenChange={setShowQuickWastage}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold text-red-400' style={{ fontFamily: 'Bebas Neue' }}>
              🗑️ Record Wastage / Damaged
            </DialogTitle>
          </DialogHeader>
          
          <div className='space-y-4'>
            <p className='text-gray-400 text-sm'>
              Go to Stock Management to log wastage or damaged items. Click the 🗑️ Waste button on any product to record wastage.
            </p>
            <Button
              onClick={() => { setShowQuickWastage(false); setShowManageProducts(true); }}
              className='w-full bg-red-500 hover:bg-red-600 text-white'
            >
              <Trash2 className='w-4 h-4 mr-2' />
              Go to Stock Management
            </Button>
            <Button
              onClick={() => setShowQuickWastage(false)}
              variant='outline'
              className='w-full border-white/20 text-white'
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* POS Product Customization Dialog */}
      <Dialog open={showPosCustomization} onOpenChange={(open) => {
        if (!open) {
          setShowPosCustomization(false);
          setCustomizingProduct(null);
          setPosSelectedAddons([]);
          setPosSelectedCustomizations({});
          setPosItemNotes('');
        }
      }}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto'>
          {customizingProduct && (
            <>
              <DialogHeader>
                <DialogTitle className='text-xl font-bold' style={{ fontFamily: 'Bebas Neue' }}>
                  🔥 Customize: {customizingProduct.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className='space-y-4'>
                {/* Product Info */}
                <div className='flex items-center gap-3 bg-white/5 rounded-lg p-3'>
                  <img 
                    src={getImageUrl(customizingProduct.image)} 
                    alt={customizingProduct.name}
                    className='w-16 h-16 rounded-lg object-cover'
                  />
                  <div>
                    <h3 className='text-white font-semibold'>{customizingProduct.name}</h3>
                    <p className='text-[#00E5FF] font-bold'>{formatCurrency(customizingProduct.price)}</p>
                  </div>
                </div>
                
                {/* Add-ons Section - Use effective_addons from global system if available */}
                {((customizingProduct.effective_addons && customizingProduct.effective_addons.length > 0) ||
                  (customizingProduct.addons && customizingProduct.addons.length > 0)) && (
                  <div className='border border-white/10 rounded-lg p-3'>
                    <h4 className='text-white font-semibold mb-3 flex items-center gap-2'>
                      <span className='text-[#FF4500]'>🔥</span> Add Finishes
                    </h4>
                    <div className='space-y-2'>
                      {(customizingProduct.effective_addons || customizingProduct.addons || []).map((addon, idx) => (
                        <label 
                          key={idx}
                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                            posSelectedAddons.some(a => a.name === addon.name)
                              ? 'bg-[#FF4500]/20 border border-[#FF4500]/50'
                              : 'bg-white/5 border border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className='flex items-center gap-2'>
                            <input
                              type='checkbox'
                              checked={posSelectedAddons.some(a => a.name === addon.name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPosSelectedAddons([...posSelectedAddons, addon]);
                                } else {
                                  setPosSelectedAddons(posSelectedAddons.filter(a => a.name !== addon.name));
                                }
                              }}
                              className='w-4 h-4 accent-[#FF4500]'
                            />
                            <span className='text-white text-sm'>{addon.name}</span>
                          </div>
                          <span className='text-[#00E5FF] text-sm font-medium'>+{formatCurrency(addon.price)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Customization Groups Section - Use effective_customization_groups from global system if available */}
                {((customizingProduct.effective_customization_groups && customizingProduct.effective_customization_groups.length > 0) ||
                  (customizingProduct.customization_groups && customizingProduct.customization_groups.length > 0)) && (
                  <div className='space-y-3'>
                    {(customizingProduct.effective_customization_groups || customizingProduct.customization_groups || []).map((group, gIdx) => (
                      <div key={gIdx} className='border border-white/10 rounded-lg p-3'>
                        <h4 className='text-white font-semibold mb-3 flex items-center gap-2'>
                          <span className='text-[#00E5FF]'>⚡</span>
                          {group.name}
                          {group.required && <span className='text-red-400 text-xs'>(Required)</span>}
                        </h4>
                        <div className='space-y-2'>
                          {(group.options || []).map((option, oIdx) => (
                            <label 
                              key={oIdx}
                              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                                posSelectedCustomizations[group.name]?.name === option.name
                                  ? 'bg-[#00E5FF]/20 border border-[#00E5FF]/50'
                                  : 'bg-white/5 border border-white/10 hover:border-white/30'
                              }`}
                            >
                              <div className='flex items-center gap-2'>
                                <input
                                  type='radio'
                                  name={`pos-custom-${gIdx}`}
                                  checked={posSelectedCustomizations[group.name]?.name === option.name}
                                  onChange={() => {
                                    setPosSelectedCustomizations({
                                      ...posSelectedCustomizations,
                                      [group.name]: option
                                    });
                                  }}
                                  className='w-4 h-4 accent-[#00E5FF]'
                                />
                                <span className='text-white text-sm'>{option.name}</span>
                              </div>
                              {option.price > 0 && (
                                <span className='text-[#00E5FF] text-sm font-medium'>+{formatCurrency(option.price)}</span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Special Instructions */}
                <div>
                  <Label className='text-white text-sm mb-2 block'>Special Instructions</Label>
                  <textarea
                    value={posItemNotes}
                    onChange={(e) => setPosItemNotes(e.target.value)}
                    placeholder='E.g., Extra spicy, no onions...'
                    className='w-full bg-white/5 border border-white/10 rounded-lg p-2 text-white text-sm placeholder-gray-500 focus:border-[#FF4500]/50 focus:outline-none'
                    rows={2}
                  />
                </div>
                
                {/* Total and Add Button */}
                <div className='flex items-center justify-between pt-3 border-t border-white/10'>
                  <div>
                    <span className='text-gray-400 text-xs block'>Total</span>
                    <span className='text-2xl font-bold text-[#FF4500]'>
                      {formatCurrency(
                        customizingProduct.price + 
                        posSelectedAddons.reduce((sum, a) => sum + (a.price || 0), 0) +
                        Object.values(posSelectedCustomizations).reduce((sum, c) => sum + (c.price || 0), 0)
                      )}
                    </span>
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      onClick={() => setShowPosCustomization(false)}
                      className='border-white/20 text-white'
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        // Check required customizations - use effective_customization_groups if available
                        const customizationGroups = customizingProduct.effective_customization_groups || customizingProduct.customization_groups || [];
                        const missingRequired = customizationGroups
                          .filter(g => g.required && !posSelectedCustomizations[g.name]);
                        
                        if (missingRequired.length > 0) {
                          toast.error(`Select: ${missingRequired.map(g => g.name).join(', ')}`);
                          return;
                        }
                        
                        addToCart(customizingProduct, posSelectedAddons, posSelectedCustomizations, posItemNotes);
                        setShowPosCustomization(false);
                        setCustomizingProduct(null);
                        setPosSelectedAddons([]);
                        setPosSelectedCustomizations({});
                        setPosItemNotes('');
                      }}
                      className='bg-[#FF4500] hover:bg-[#FF5722] text-white'
                    >
                      Add to Cart
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Staff Switcher - Quick switch between POS/Kitchen/Admin */}
      <StaffSwitcher />

      {/* Offers Popup */}
      <Dialog open={showOfferPopup} onOpenChange={setShowOfferPopup}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md'>
          <DialogHeader>
            <DialogTitle className='text-2xl font-bold text-center' style={{ fontFamily: 'Bebas Neue' }}>
              <span className='text-[#FF4500]'>🎉 Special Offer!</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className='space-y-4'>
            {eligibleOffers.filter(o => o.offer_type === 'combo_deal' || o.offer_type === 'bogo').map(offer => (
              <div key={offer.id} className='bg-white/5 rounded-lg p-4 border border-[#FF4500]/30'>
                <h3 className='text-white font-semibold text-lg mb-2'>{offer.name}</h3>
                {offer.description && (
                  <p className='text-gray-400 text-sm mb-3'>{offer.description}</p>
                )}
                
                {offer.reward?.product_name && (
                  <div className='bg-[#FF4500]/10 rounded-lg p-3 mb-3'>
                    <p className='text-gray-400 text-xs uppercase'>Add this to your order</p>
                    <p className='text-white font-medium text-lg'>{offer.reward.product_name}</p>
                    <div className='flex items-center gap-2 mt-1'>
                      {offer.reward.original_price && (
                        <span className='text-gray-500 line-through'>₹{offer.reward.original_price}</span>
                      )}
                      <span className='text-[#FF4500] font-bold text-2xl'>₹{offer.reward.special_price}</span>
                    </div>
                  </div>
                )}
                
                <Button
                  onClick={() => applyComboOffer(offer)}
                  className='w-full bg-[#FF4500] hover:bg-[#FF4500]/80 text-white font-bold py-3'
                >
                  Add to Order
                </Button>
              </div>
            ))}
            
            <Button
              variant='ghost'
              onClick={() => setShowOfferPopup(false)}
              className='w-full text-gray-400 hover:text-white'
            >
              No Thanks
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Offers Panel Button - Show on cart section */}
      {activeOffers.length > 0 && cart.length > 0 && (
        <div className='fixed bottom-20 right-4 z-40 md:hidden'>
          <Button
            onClick={() => setShowOffersPanel(true)}
            className='bg-[#FF4500] hover:bg-[#FF4500]/80 rounded-full p-3 shadow-lg animate-pulse'
          >
            <Gift className='w-5 h-5' />
            {eligibleOffers.length > 0 && (
              <span className='absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full text-xs flex items-center justify-center'>
                {eligibleOffers.length}
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Offers Panel Dialog */}
      <Dialog open={showOffersPanel} onOpenChange={setShowOffersPanel}>
        <DialogContent className='bg-[#121212] border-white/10 text-white max-w-md max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold' style={{ fontFamily: 'Bebas Neue' }}>
              Available Offers
            </DialogTitle>
          </DialogHeader>
          
          <div className='space-y-3'>
            {/* Applied Offers */}
            {appliedOffers.length > 0 && (
              <div className='mb-4'>
                <p className='text-green-400 text-sm font-medium mb-2'>✓ Applied Offers</p>
                {appliedOffers.map(offer => (
                  <div key={offer.id} className='flex items-center justify-between bg-green-500/10 rounded-lg p-3 mb-2'>
                    <span className='text-white text-sm'>{offer.name}</span>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => removeAppliedOffer(offer.id)}
                      className='text-red-400 hover:text-red-300 px-2'
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Eligible Offers */}
            {eligibleOffers.length > 0 ? (
              eligibleOffers.map(offer => (
                <div key={offer.id} className='bg-white/5 rounded-lg p-3 border border-white/10'>
                  <div className='flex items-start justify-between mb-2'>
                    <div>
                      <h4 className='text-white font-medium'>{offer.name}</h4>
                      {offer.description && (
                        <p className='text-gray-400 text-xs mt-1'>{offer.description}</p>
                      )}
                    </div>
                    <Badge className='bg-[#FF4500]/20 text-[#FF4500]'>
                      {offer.offer_type === 'combo_deal' ? 'Combo' : 
                       offer.offer_type === 'percentage' ? '% Off' : 
                       offer.offer_type === 'flat_discount' ? '₹ Off' : 'BOGO'}
                    </Badge>
                  </div>
                  
                  {offer.reward?.product_name && offer.reward?.special_price && (
                    <p className='text-[#FF4500] text-sm mb-2'>
                      Get {offer.reward.product_name} @ ₹{offer.reward.special_price}
                    </p>
                  )}
                  
                  <Button
                    onClick={() => {
                      if (offer.offer_type === 'combo_deal' || offer.offer_type === 'bogo') {
                        applyComboOffer(offer);
                      }
                      setShowOffersPanel(false);
                    }}
                    className='w-full bg-[#FF4500] hover:bg-[#FF4500]/80 text-sm py-2'
                    size='sm'
                  >
                    Apply Offer
                  </Button>
                </div>
              ))
            ) : (
              <div className='text-center py-6'>
                <Gift className='w-10 h-10 mx-auto text-gray-500 mb-2' />
                <p className='text-gray-400'>No eligible offers for current cart</p>
                <p className='text-gray-500 text-xs mt-1'>Add more items to unlock offers</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSSystem;
